import { useState, useRef, useEffect } from "react";
import { auth, googleProvider, db, gemini } from "./firebase";
import { SCENE_MENU, SCENE_DIALOGUES } from "./roleplayData";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import {
  collection, getDocs, query, where, limit,
  doc, setDoc, getDoc, serverTimestamp
} from "firebase/firestore";

const OWNER_EMAIL = "yoshiharu.sato@accenture.com";

const BLUE       = "#4f6ef7";
const BLUE_LIGHT = "#eef1ff";
const BORDER     = "#e8e8e8";
const CARD       = "#ffffff";
const BG         = "#f7f8fc";
const GREEN      = "#22c55e";
const RED        = "#ef4444";

const COUNTRY_GROUPS = [
  {
    label: "AVAILABLE NOW",
    countries: [
      { c: "gb", n: "UK",        f: "🇬🇧" },
      { c: "fr", n: "France",    f: "🇫🇷" },
      { c: "de", n: "Germany",   f: "🇩🇪" },
      { c: "it", n: "Italy",     f: "🇮🇹" },
      { c: "es", n: "Spain",     f: "🇪🇸" },
      { c: "sg", n: "Singapore", f: "🇸🇬" },
      { c: "us", n: "USA",       f: "🇺🇸" },
      { c: "au", n: "Australia", f: "🇦🇺" },
    ],
  },
  {
    label: "ASIA", soon: true,
    countries: [
      { c: "jp", n: "Japan",       f: "🇯🇵" },
      { c: "cn", n: "China",       f: "🇨🇳" },
      { c: "kr", n: "Korea",       f: "🇰🇷" },
      { c: "in", n: "India",       f: "🇮🇳" },
      { c: "th", n: "Thailand",    f: "🇹🇭" },
      { c: "vn", n: "Vietnam",     f: "🇻🇳" },
      { c: "id", n: "Indonesia",   f: "🇮🇩" },
      { c: "my", n: "Malaysia",    f: "🇲🇾" },
      { c: "ph", n: "Philippines", f: "🇵🇭" },
    ],
  },
  {
    label: "EUROPE", soon: true,
    countries: [
      { c: "nl", n: "Netherlands", f: "🇳🇱" },
      { c: "se", n: "Sweden",      f: "🇸🇪" },
      { c: "no", n: "Norway",      f: "🇳🇴" },
      { c: "ch", n: "Switzerland", f: "🇨🇭" },
      { c: "pl", n: "Poland",      f: "🇵🇱" },
    ],
  },
  {
    label: "AMERICAS", soon: true,
    countries: [
      { c: "ca", n: "Canada",    f: "🇨🇦" },
      { c: "br", n: "Brazil",    f: "🇧🇷" },
      { c: "mx", n: "Mexico",    f: "🇲🇽" },
      { c: "ar", n: "Argentina", f: "🇦🇷" },
    ],
  },
  {
    label: "OCEANIA", soon: true,
    countries: [
      { c: "nz", n: "New Zealand", f: "🇳🇿" },
    ],
  },
];

const AVAILABLE_COUNTRIES = COUNTRY_GROUPS[0].countries;

const GROUPS = [
  {
    id: "work", label: "Work", ja_label: "ビジネス", ic: "💼", color: "#fef9ee",
    desc: "Meetings · Emails · Dining · Negotiation",
    ja_desc: "会議・メール・接待・交渉",
    subs: [
      { id: "biz_card",        label: "Business Cards",   ja_label: "名刺交換",     ic: "🪪" },
      { id: "biz_meeting",     label: "Meetings",         ja_label: "会議",         ic: "🤝" },
      { id: "biz_email",       label: "Emails",           ja_label: "メール",       ic: "📧" },
      { id: "biz_dining",      label: "Business Dining",  ja_label: "接待",         ic: "🍽️" },
      { id: "biz_smalltalk",   label: "Small Talk",       ja_label: "雑談",         ic: "💬" },
      { id: "biz_negotiation", label: "Negotiation",      ja_label: "交渉",         ic: "🤜" },
      { id: "biz_gift",        label: "Gifts",            ja_label: "贈り物",       ic: "🎁" },
      { id: "biz_culture",     label: "Business Culture", ja_label: "ビジネス文化", ic: "🏢" },
    ],
  },
  {
    id: "life", label: "Life", ja_label: "生活", ic: "🏠", color: "#f0fdf4",
    desc: "Neighbours · School · Healthcare · Shopping",
    ja_desc: "近所づき合い・学校・医療・買い物",
    subs: [
      { id: "life_neighbor",    label: "Neighbours",       ja_label: "近所づき合い",   ic: "🏘️" },
      { id: "life_school",      label: "School (General)", ja_label: "学校（一般）",   ic: "🏫" },
      { id: "life_school_elem", label: "Primary School",   ja_label: "小学校",         ic: "🎒" },
      { id: "life_school_high", label: "Secondary School", ja_label: "中高校",         ic: "📚" },
      { id: "life_medical",     label: "Healthcare",       ja_label: "医療",           ic: "🏥" },
      { id: "life_shopping",    label: "Shopping",         ja_label: "買い物",         ic: "🛒" },
      { id: "life_transport",   label: "Transport",        ja_label: "交通",           ic: "🚌" },
      { id: "life_housing",     label: "Housing",          ja_label: "住居",           ic: "🏠" },
      { id: "life_money",       label: "Money & Banking",  ja_label: "お金・銀行",     ic: "🏦" },
      { id: "life_friends",     label: "Friends",          ja_label: "友人関係",       ic: "👥" },
      { id: "life_romance",     label: "Romance",          ja_label: "恋愛",           ic: "❤️" },
      { id: "life_digital",     label: "Digital & Social", ja_label: "デジタル・SNS", ic: "📱" },
    ],
  },
  {
    id: "travel", label: "Travel", ja_label: "旅行", ic: "✈️", color: "#eff6ff",
    desc: "Hotels · Restaurants · Sightseeing · Transport",
    ja_desc: "ホテル・レストラン・観光・交通",
    subs: [
      { id: "travel_hotel",       label: "Hotels",       ja_label: "ホテル", ic: "🏨" },
      { id: "travel_restaurant",  label: "Restaurants",  ja_label: "レストラン", ic: "🍴" },
      { id: "travel_sightseeing", label: "Sightseeing",  ja_label: "観光",   ic: "📸" },
      { id: "travel_transport",   label: "Transport",    ja_label: "交通",   ic: "🚇" },
      { id: "travel_driving",     label: "Driving",      ja_label: "ドライブ", ic: "🚗" },
      { id: "travel_cycling",     label: "Cycling",      ja_label: "サイクリング", ic: "🚴" },
      { id: "travel_emergency",   label: "Emergencies",  ja_label: "緊急事態", ic: "🚨" },
    ],
  },
  {
    id: "culture", label: "Culture", ja_label: "文化", ic: "🌍", color: "#fdf4ff",
    desc: "History · Religion · Politics · Sports · Media",
    ja_desc: "歴史・宗教・政治・スポーツ・メディア",
    subs: [
      { id: "culture_history",   label: "History",      ja_label: "歴史",     ic: "🏛️" },
      { id: "culture_geography", label: "Geography",    ja_label: "地理",     ic: "🗺️" },
      { id: "culture_religion",  label: "Religion",     ja_label: "宗教",     ic: "⛪" },
      { id: "culture_holidays",  label: "Holidays",     ja_label: "祝日",     ic: "🎉" },
      { id: "culture_politics",  label: "Politics",     ja_label: "政治",     ic: "🗳️" },
      { id: "culture_sport",     label: "Sports",       ja_label: "スポーツ", ic: "⚽" },
      { id: "culture_food",      label: "Food Culture", ja_label: "食文化",   ic: "🍺" },
      { id: "culture_media",     label: "Media",        ja_label: "メディア", ic: "📺" },
      { id: "culture_education", label: "Education",    ja_label: "教育",     ic: "🎓" },
      { id: "culture_industry",  label: "Industry",     ja_label: "産業",     ic: "🏭" },
    ],
  },
];

const STAGES = [
  { id: 1, label: "Stage 1", ja_label: "ステージ1", levels: [1, 2] },
  { id: 2, label: "Stage 2", ja_label: "ステージ2", levels: [3, 4] },
  { id: 3, label: "Stage 3", ja_label: "ステージ3", levels: [5, 6] },
  { id: 4, label: "Stage 4", ja_label: "ステージ4", levels: [7, 8] },
  { id: 5, label: "Stage 5", ja_label: "ステージ5", levels: [9, 10] },
];

const LEVEL_TITLES = [
  "", "🌱 Newcomer", "📚 Student", "🎒 Traveller", "🏠 Resident",
  "💼 Practitioner", "🎓 Graduate", "🔬 Scholar", "📖 Professor",
  "🏛️ Dean", "👑 Grand Master",
];

const mkCats = (cats) => cats;

const PREP_CHECKLISTS = {
  gb: {
    label: "UK Prep Checklist",
    tabs: [
      {
        id: "travel", label: "Travel / Business",
        categories: mkCats([
          { emoji: "📋", label: "Documents & Entry", items: [
            "Check passport validity (6+ months from travel date)",
            "Confirm visa requirements (UK ETA if applicable from Jan 2025)",
            "Apply for UK ETA online if required (£10 fee)",
            "Save passport copy separately (email to yourself)",
            "Carry printed hotel/address confirmation",
          ]},
          { emoji: "💳", label: "Money & Banking", items: [
            "Exchange small amount of GBP cash for emergencies",
            "Notify your bank of overseas travel dates",
            "Prepare international credit/debit card (Visa/Mastercard)",
            "Check overseas transaction & ATM fees with your bank",
            "Download Wise or Revolut for better exchange rates",
          ]},
          { emoji: "📱", label: "Connectivity", items: [
            "Arrange local SIM or eSIM (EE, O2, Vodafone etc.)",
            "Download Google Maps offline for your destination",
            "Download Citymapper for London/Manchester transit",
            "Download the National Rail app for train travel",
          ]},
          { emoji: "🏨", label: "Accommodation", items: [
            "Confirm accommodation booking & save confirmation email",
            "Note hotel check-in time and late arrival procedure",
            "Research airport → accommodation transport options",
            "Note nearest tube/bus stop to your accommodation",
          ]},
          { emoji: "🚇", label: "Transport", items: [
            "Get Oyster card or set up contactless payment for TfL",
            "Book National Rail tickets in advance (30%+ cheaper)",
            "Check Heathrow/Gatwick Express vs Elizabeth line costs",
            "Understand UK driving on the left (if renting a car)",
            "Download Uber or Bolt app as taxi backup",
          ]},
          { emoji: "🏥", label: "Health & Safety", items: [
            "Purchase travel insurance (medical cover required)",
            "Check NHS access eligibility (GHIC card for EU citizens)",
            "Pack sufficient prescription medication (+ doctor's letter)",
            "Note walk-in clinic / urgent care locations near you",
            "Save emergency number: 999 (police/fire/ambulance)",
            "Note non-emergency police: 101",
          ]},
          { emoji: "🔌", label: "Tech & Gadgets", items: [
            "Pack travel adapter (UK Type G — 3-pin square)",
            "Charge all devices before departure",
            "Check if your phone supports UK LTE bands",
          ]},
          { emoji: "💼", label: "Business Prep", items: [
            "Prepare business cards (English version)",
            "Research business dress code (smart casual common)",
            "Practise key British business English phrases",
            "Learn British meeting etiquette (punctuality, small talk)",
            "Understand UK tipping culture (10–15% at restaurants)",
            "Research your UK counterparts / company background",
            "Confirm meeting locations & travel time",
            "Set up VPN if accessing company systems remotely",
          ]},
          { emoji: "🎭", label: "Cultural Prep", items: [
            "Learn British weather talk (always a safe topic!)",
            "Understand pub etiquette (rounds, bar ordering)",
            "Read up on UK current affairs / football (conversation starters)",
            "Know tea preferences (milk before/after, strong/weak)",
            "Understand queue culture — always queue, never cut in",
          ]},
          { emoji: "✈️", label: "Final Checks", items: [
            "Check luggage & baggage allowance for your airline",
            "Check weather forecast & pack layers (UK weather = unpredictable)",
            "Pack compact umbrella (essential in UK)",
            "Check in online (24–48hrs before flight)",
            "Arrive at airport 3hrs before international departure",
            "Note JP Embassy London for emergencies: +44-20-7465-6500",
          ]},
        ]),
      },
      {
        id: "relocation", label: "Relocation / Long-term",
        categories: mkCats([
          { emoji: "📋", label: "Visa & Legal", items: [
            "Determine correct visa category (Skilled Worker / Student / Family)",
            "Apply for UK visa with full supporting documents",
            "Apply for Biometric Residence Permit (BRP) after arrival",
            "Register with local council (council tax registration)",
            "Check right-to-work documentation requirements",
          ]},
          { emoji: "🏠", label: "Housing", items: [
            "Research rental areas using Rightmove / Zoopla",
            "Arrange short-term accommodation for first 2–4 weeks",
            "Review tenancy agreement terms carefully",
            "Arrange contents insurance for your belongings",
            "Set up utilities (gas, electric, water, broadband)",
            "Register for council tax at your address",
          ]},
          { emoji: "💳", label: "Finance", items: [
            "Open a UK bank account (Monzo / Starling / HSBC)",
            "Apply for National Insurance (NI) number",
            "Set up standing orders for rent and utilities",
            "File Self Assessment tax return if needed",
            "Open a Lifetime ISA or pension plan",
          ]},
          { emoji: "📱", label: "Connectivity", items: [
            "Sign up for a monthly mobile contract",
            "Set up UK broadband at home",
            "Get a UK driver's licence (convert from your country's licence)",
          ]},
          { emoji: "🏥", label: "NHS & Health", items: [
            "Register with a local GP (doctor)",
            "Register with an NHS dentist",
            "Check NHS entitlement (linked to visa type)",
            "Pay Immigration Health Surcharge if applicable",
          ]},
          { emoji: "🎓", label: "Integration", items: [
            "Join local community groups or expat networks",
            "Learn about local council services",
            "Understand UK recycling rules",
            "Explore nearby parks, supermarkets, and transport links",
          ]},
        ]),
      },
      {
        id: "assignment", label: "Assignment / Work",
        categories: mkCats([
          { emoji: "📋", label: "Work Authorization", items: [
            "Confirm employer sponsorship certificate (CoS)",
            "Obtain Skilled Worker visa before departure",
            "Carry all employment contracts and offer letters",
            "Understand right-to-work verification process",
            "Register BRP within 10 days of arrival",
          ]},
          { emoji: "💰", label: "Payroll & Tax", items: [
            "Apply for National Insurance (NI) number promptly",
            "Receive correct UK tax code from employer",
            "Set up UK bank account for salary deposits",
            "Understand P60 / P45 payslip documents",
            "Clarify company expenses and allowances policy",
          ]},
          { emoji: "🏠", label: "Accommodation", items: [
            "Confirm whether company provides housing allowance",
            "Arrange corporate housing or serviced apartment",
            "Understand lease break clauses for flexibility",
            "Research transport links from home to office",
          ]},
          { emoji: "💼", label: "Work Setup", items: [
            "Receive company ID, access card, and IT equipment",
            "Set up corporate email and collaboration tools",
            "Join company VPN and security protocols",
            "Meet HR and understand UK employment rights",
            "Understand notice period requirements (statutory minimum 1 week/year)",
          ]},
          { emoji: "🏥", label: "Health & Welfare", items: [
            "Check company private health insurance (BUPA etc.)",
            "Register with a local GP",
            "Understand Employee Assistance Programme (EAP)",
            "Know sick leave entitlement (28 days + bank holidays statutory)",
          ]},
          { emoji: "🎭", label: "Cultural Adaptation", items: [
            "Learn British workplace communication style (indirect, polite)",
            "Understand after-work pub culture (social norm)",
            "Learn about UK bank holidays and office closures",
            "Read up on UK workplace equality and D&I policies",
          ]},
        ]),
      },
    ],
  },
  us: {
    label: "US Prep Checklist",
    tabs: [{
      id: "travel", label: "Travel / Business",
      categories: mkCats([
        { emoji: "📋", label: "Documents & Entry", items: [
          "Check passport validity",
          "Apply for ESTA (tourism/business ≤90 days) or obtain visa",
          "Confirm US visa requirements for your nationality",
        ]},
        { emoji: "💳", label: "Money & Banking", items: [
          "Arrange USD cash or international credit card",
          "Confirm health insurance coverage abroad",
          "Research state-specific tax rates on purchases",
        ]},
        { emoji: "📱", label: "Connectivity", items: [
          "Arrange local SIM or eSIM (T-Mobile, AT&T etc.)",
          "Download Google Maps / Waze for navigation",
        ]},
        { emoji: "🏨", label: "Accommodation", items: [
          "Confirm accommodation booking",
          "Note hotel amenities (US hotels often have free breakfast)",
        ]},
        { emoji: "🚗", label: "Transport", items: [
          "Research transport (Uber/Lyft/subway/rental car)",
          "Check time zones (varies by state: EST/PST etc.)",
          "International Driving Permit if renting a car",
        ]},
        { emoji: "🏥", label: "Health & Safety", items: [
          "Purchase travel insurance (US medical costs are very high)",
          "Note emergency number: 911",
        ]},
        { emoji: "🔌", label: "Tech & Gadgets", items: [
          "Pack travel adapter (US Type A — same plug shape as Japan)",
        ]},
        { emoji: "💼", label: "Business Prep", items: [
          "Prepare business cards (English version)",
          "Learn tipping culture (15–20% at restaurants, 15–20% for taxis)",
          "Understand informal US meeting style",
        ]},
      ]),
    }],
  },
  fr: {
    label: "France Prep Checklist",
    tabs: [{
      id: "travel", label: "Travel / Business",
      categories: mkCats([
        { emoji: "📋", label: "Documents & Entry", items: [
          "Check passport validity",
          "Confirm Schengen visa requirements if applicable",
          "Check entry requirements (post-Brexit rules for UK citizens)",
        ]},
        { emoji: "💳", label: "Money & Banking", items: [
          "Exchange EUR cash",
          "Confirm credit card acceptance (chip & PIN standard)",
        ]},
        { emoji: "📱", label: "Connectivity", items: [
          "Arrange European SIM or eSIM",
          "Download SNCF app for train travel",
          "Download Paris Metro app (RATP)",
        ]},
        { emoji: "🏥", label: "Health & Safety", items: [
          "Purchase travel insurance",
          "Note emergency number: 112 (EU-wide)",
          "Note SAMU (medical): 15, Police: 17, Fire: 18",
        ]},
        { emoji: "🔌", label: "Tech & Gadgets", items: [
          "Pack travel adapter (EU Type C — round 2-pin)",
        ]},
        { emoji: "💼", label: "Cultural Prep", items: [
          "Practise basic French greetings (greeting in French is important)",
          "Learn tipping etiquette (not expected but appreciated for good service)",
          "Understand French business formality (use Monsieur/Madame)",
        ]},
      ]),
    }],
  },
  de: {
    label: "Germany Prep Checklist",
    tabs: [{
      id: "travel", label: "Travel / Business",
      categories: mkCats([
        { emoji: "📋", label: "Documents & Entry", items: [
          "Check passport validity",
          "Confirm Schengen visa requirements if applicable",
        ]},
        { emoji: "💳", label: "Money & Banking", items: [
          "Exchange EUR cash (many places still cash-only in Germany)",
          "Confirm credit card acceptance at your destination",
        ]},
        { emoji: "📱", label: "Connectivity", items: [
          "Arrange European SIM or eSIM",
          "Download DB Navigator app for train travel",
        ]},
        { emoji: "🏥", label: "Health & Safety", items: [
          "Purchase travel insurance",
          "Note emergency number: 112",
        ]},
        { emoji: "🔌", label: "Tech & Gadgets", items: [
          "Pack travel adapter (EU Type C/F — round 2-pin)",
        ]},
        { emoji: "💼", label: "Business & Cultural Prep", items: [
          "Learn punctuality customs (being on time is critical in Germany)",
          "Prepare business cards (English and German if possible)",
          "Understand Sunday trading restrictions (most shops closed)",
          "Learn formal address customs (use Herr/Frau + surname)",
        ]},
      ]),
    }],
  },
  it: {
    label: "Italy Prep Checklist",
    tabs: [{
      id: "travel", label: "Travel / Business",
      categories: mkCats([
        { emoji: "📋", label: "Documents & Entry", items: [
          "Check passport validity",
          "Confirm Schengen visa requirements if applicable",
        ]},
        { emoji: "💳", label: "Money & Banking", items: [
          "Exchange EUR cash (useful for smaller towns and markets)",
          "Confirm credit card acceptance",
        ]},
        { emoji: "📱", label: "Connectivity", items: [
          "Arrange European SIM or eSIM",
          "Download Trenitalia app for train travel",
        ]},
        { emoji: "🏥", label: "Health & Safety", items: [
          "Purchase travel insurance",
          "Note emergency number: 112 / 113 (Police) / 118 (Ambulance)",
        ]},
        { emoji: "🔌", label: "Tech & Gadgets", items: [
          "Pack travel adapter (EU Type C/F/L — check your destination's socket type)",
        ]},
        { emoji: "🎭", label: "Cultural Prep", items: [
          "Practise basic Italian greetings (Buongiorno/Ciao)",
          "Learn dress code for churches & historic sites (cover shoulders/knees)",
          "Research coperto (cover charge) at restaurants — normal practice",
          "Understand mealtimes (lunch 1–3pm, dinner from 8pm)",
        ]},
      ]),
    }],
  },
  sg: {
    label: "Singapore Prep Checklist",
    tabs: [{
      id: "travel", label: "Travel / Business",
      categories: mkCats([
        { emoji: "📋", label: "Documents & Entry", items: [
          "Check passport validity (6+ months)",
          "Confirm visa-free status or apply for visa",
        ]},
        { emoji: "💳", label: "Money & Banking", items: [
          "Exchange SGD or use credit card (widely accepted)",
          "Download GrabPay or PayNow for local payments",
        ]},
        { emoji: "📱", label: "Connectivity", items: [
          "Arrange local SIM or eSIM (Singtel, StarHub, M1)",
          "Download Grab app (ride-hailing + food delivery)",
          "Research MRT transit app (SimplyGo)",
        ]},
        { emoji: "🏥", label: "Health & Safety", items: [
          "Purchase travel insurance",
          "Note emergency number: 999 (Police), 995 (Fire/Ambulance)",
          "Note strict laws (fines for littering, chewing gum banned, jaywalking)",
        ]},
        { emoji: "🔌", label: "Tech & Gadgets", items: [
          "Pack travel adapter (UK Type G — same plugs as Singapore)",
        ]},
        { emoji: "🎭", label: "Cultural Prep", items: [
          "Learn basic Singlish phrases (lah, leh, lor)",
          "Understand hawker centre etiquette (tissue paper reserving seats)",
          "Respect racial and religious harmony — avoid sensitive topics",
        ]},
      ]),
    }],
  },
  au: {
    label: "Australia Prep Checklist",
    tabs: [{
      id: "travel", label: "Travel / Business",
      categories: mkCats([
        { emoji: "📋", label: "Documents & Entry", items: [
          "Check passport validity (6+ months)",
          "Apply for ETA or eVisitor visa (AU$20 fee)",
          "Note strict biosecurity — declare all food, plants, animal products",
        ]},
        { emoji: "💳", label: "Money & Banking", items: [
          "Arrange AUD cash or international card",
          "Confirm credit card acceptance (contactless widely used)",
        ]},
        { emoji: "📱", label: "Connectivity", items: [
          "Arrange local SIM or eSIM (Telstra, Optus, Vodafone)",
          "Download Google Maps offline (large distances, possible no signal)",
        ]},
        { emoji: "🏥", label: "Health & Safety", items: [
          "Purchase travel insurance",
          "Note emergency number: 000",
          "Pack high-SPF sunscreen (UV index very high in Australia)",
        ]},
        { emoji: "🔌", label: "Tech & Gadgets", items: [
          "Pack travel adapter (AU/NZ Type I — angled 3-pin)",
        ]},
        { emoji: "🎭", label: "Cultural Prep", items: [
          "Learn Australian tipping culture (not mandatory but appreciated)",
          "Understand relaxed Aussie communication style",
          "Check local wildlife safety (snakes, spiders in rural areas)",
        ]},
      ]),
    }],
  },
};




const SESSION_SIZE  = 5;
const CLEAR_THRESH  = 4;

async function fetchSessionQuestions(country, category, stageId) {
  try {
    const [minLv, maxLv] = STAGES[stageId - 1].levels;
    const q = query(
      collection(db, "questions"),
      where("country", "==", country),
      where("category", "==", category),
      limit(300)
    );
    const snap = await getDocs(q);
    const all = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(doc => doc.active !== false && doc.level >= minLv && doc.level <= maxLv);
    return { ok: true, data: [...all].sort(() => Math.random() - 0.5).slice(0, SESSION_SIZE).map(q => {
      const perm = [0, 1, 2, 3].sort(() => Math.random() - 0.5);
      return { ...q, choices: perm.map(i => q.choices[i]), answer: perm.indexOf(q.answer) };
    }), total: all.length, raw: snap.size };
  } catch (e) {
    console.error("fetchSessionQuestions error:", e);
    return { ok: false, data: [], error: e.message || String(e) };
  }
}

async function loadUserProfile(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? snap.data() : null;
  } catch (e) { return null; }
}

async function saveUserProfile(uid, data) {
  try { await setDoc(doc(db, "users", uid), data, { merge: true }); }
  catch (e) { console.error("saveUserProfile error:", e); }
}

async function saveProgress(uid, questionId, correct, xp) {
  if (!uid) return;
  try {
    await setDoc(doc(db, "progress", uid, "answers", questionId + "_" + Date.now()), {
      questionId, correct, xpEarned: correct ? xp : 0, timestamp: serverTimestamp(),
    });
  } catch (e) { console.error("saveProgress error:", e); }
}

function xpForLevel(level) {
  if (level <= 3) return 10;
  if (level <= 7) return 30;
  return 50;
}
function levelFromXP(xp) { return Math.min(10, Math.floor(xp / 1000) + 1); }

// Plan limits
const PLAN_LIMITS = {
  free:        { quizPerDay: 20, roleplayPerDay: 1, aiPerDay: 3,  maxStage: 2, maxCountries: 2 },
  traveller:   { quizPerDay: Infinity, roleplayPerDay: Infinity, aiPerDay: 20, maxStage: 3, maxCountries: 5 },
  ambassador:  { quizPerDay: Infinity, roleplayPerDay: Infinity, aiPerDay: 60, maxStage: 5, maxCountries: Infinity },
  // legacy alias
  voyager:     { quizPerDay: Infinity, roleplayPerDay: Infinity, aiPerDay: 20, maxStage: 3, maxCountries: 5 },
};

// Daily counter helpers (localStorage, keyed by date)
function getTodayKey() { return new Date().toISOString().slice(0, 10); }
function getDailyCount(key) {
  try {
    const stored = JSON.parse(localStorage.getItem("daily_" + key) || "{}");
    if (stored.date !== getTodayKey()) return 0;
    return stored.count || 0;
  } catch { return 0; }
}
function incDailyCount(key) {
  try {
    const today = getTodayKey();
    const stored = JSON.parse(localStorage.getItem("daily_" + key) || "{}");
    const count = (stored.date === today ? stored.count || 0 : 0) + 1;
    localStorage.setItem("daily_" + key, JSON.stringify({ date: today, count }));
    return count;
  } catch { return 1; }
}

export default function App() {
  const [user,            setUser]            = useState(null);
  const [isPro,           setIsPro]           = useState(false);
  const [plan,            setPlan]            = useState("free"); // "free" | "voyager" | "ambassador"
  const [billingCycle,    setBillingCycle]    = useState("monthly"); // "monthly" | "yearly"
  const [showUpgrade,     setShowUpgrade]     = useState(false);
  const [upgradeReason,   setUpgradeReason]   = useState(null); // context for upgrade modal
  const [showCountryPick, setShowCountryPick] = useState(false);
  const [myCountries,     setMyCountries]     = useState(["gb"]);
  const [countrySearch,   setCountrySearch]   = useState("");
  const [totalXP,         setTotalXP]         = useState(0);
  const [totalCorrect,    setTotalCorrect]    = useState(0);
  const [streak,          setStreak]          = useState(0);
  const [tab,             setTab]             = useState("home");
  const [xpGain,          setXpGain]          = useState(null);  // {amount, key} for +XP animation
  const [levelUp,         setLevelUp]         = useState(null);  // new level number on level-up

  // Quiz navigation
  const [quizCountry, setQuizCountry] = useState(null);
  const [group,       setGroup]       = useState(null);
  const [subcat,      setSubcat]      = useState(null);
  const [stage,       setStage]       = useState(null);

  // Quiz session
  const [sessionQs,      setSessionQs]      = useState([]);
  const [qsLoading,      setQsLoading]      = useState(false);
  const [qsError,        setQsError]        = useState(null);
  const [qi,             setQi]             = useState(0);
  const [answered,       setAnswered]       = useState(false);
  const [chosen,         setChosen]         = useState(null);
  const [sessionResults, setSessionResults] = useState([]);
  const [showResult,     setShowResult]     = useState(false);

  // Review mode (after failed session)
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIdx,  setReviewIdx]  = useState(0);
  const [reviewAnswered, setReviewAnswered] = useState(false);
  const [reviewChosen,   setReviewChosen]   = useState(null);
  // Persists across resetToSubcat so home screen weak-points review works
  const [wrongQuestions, setWrongQuestions] = useState([]);

  // Profile / XP modal
  const [showProfile, setShowProfile] = useState(false);
  const [showXPModal, setShowXPModal] = useState(false);

  // Onboarding
  const [showOnboard, setShowOnboard] = useState(() => !localStorage.getItem("onboarded"));
  const [onboardStep, setOnboardStep] = useState(0);
  const [onboardCountry, setOnboardCountry] = useState("gb");

  // Roleplay subcategory
  const [roleplaySubcat, setRoleplaySubcat] = useState(false);
  const [roleplayScene, setRoleplayScene] = useState(null);
  const [roleplayTurnIdx, setRoleplayTurnIdx] = useState(0);
  const [roleplayChosen, setRoleplayChosen] = useState(null);
  const [roleplayAnswered, setRoleplayAnswered] = useState(false);

  // Checklist
  const [showChecklist,  setShowChecklist]  = useState(false);
  const [clTabIdx,       setClTabIdx]       = useState(0);
  const [checkedItems,   setCheckedItems]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("checkedItems") || "{}"); } catch { return {}; }
  });

  // Language
  const [appLang, setAppLang] = useState(() => {
    try { return localStorage.getItem("appLang") || "en"; } catch { return "en"; }
  });
  const setLang = (l) => { setAppLang(l); try { localStorage.setItem("appLang", l); } catch {} };
  const t = (ja, en) => appLang === "ja" ? ja : en;
  const gl = (obj) => (appLang === "ja" ? (obj?.ja_label || obj?.label) : obj?.label) || "";
  const gd = (obj) => (appLang === "ja" ? (obj?.ja_desc || obj?.desc) : obj?.desc) || "";

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem("darkMode") === "1"; } catch { return false; }
  });
  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    try { localStorage.setItem("darkMode", next ? "1" : "0"); } catch {}
  };
  useEffect(() => { document.body.style.background = darkMode ? "#0e0e14" : "#f7f8fc"; }, [darkMode]);

  // Dynamic color tokens (shadow file-level constants for dark mode)
  // eslint-disable-next-line no-shadow
  const BG         = darkMode ? "#0e0e14" : "#f7f8fc";
  // eslint-disable-next-line no-shadow
  const CARD       = darkMode ? "#1a1a24" : "#ffffff";
  // eslint-disable-next-line no-shadow
  const BORDER     = darkMode ? "#2c2c3c" : "#e8e8e8";
  // eslint-disable-next-line no-shadow
  const BLUE_LIGHT = darkMode ? "#1e2456" : "#eef1ff";
  const TEXT1      = darkMode ? "#f0f0f0" : "#1a1a1a";
  const TEXT2      = darkMode ? "#b0b0c0" : "#555";
  const TEXT3      = darkMode ? "#7a7a90" : "#888";
  const TEXT4      = darkMode ? "#505068" : "#aaa";
  const SUBTLE     = darkMode ? "#252535" : "#f8f8f8";

  // New screens & modals
  const [showTerms,          setShowTerms]          = useState(false);
  const [showPrivacy,        setShowPrivacy]         = useState(false);
  const [showLearningGoals,  setShowLearningGoals]   = useState(false);
  const [showAddToHome,      setShowAddToHome]        = useState(false);
  const [learningPurpose,    setLearningPurpose]      = useState("business");
  const [streakGoal,         setStreakGoal]           = useState(365);
  const [aiRemaining,        setAiRemaining]          = useState(3);

  // AI chat
  const [messages,  setMessages]  = useState([
    { role: "assistant", text: "Hello! Ask me anything about business culture, daily life, or travel etiquette around the world." }
  ]);
  const [input,    setInput]    = useState("");
  const [aiLoading,setAiLoading]= useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const profile = await loadUserProfile(u.uid);
        if (profile) {
          setTotalXP(profile.totalXP || 0);
          setTotalCorrect(profile.totalCorrect || 0);
          setStreak(profile.streak || 0);
          setIsPro(profile.isPro || false);
          setPlan(profile.plan || "free");
        } else {
          await saveUserProfile(u.uid, {
            displayName: u.displayName, email: u.email,
            totalXP: 0, level: 1, streak: 0, isPro: false, plan: "free",
            createdAt: new Date().toISOString(),
          });
        }
      }
    });
    return unsub;
  }, []);

  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
  const canUpgrade = plan !== "ambassador";

  function checkLimit(type, reason) {
    const count = getDailyCount(type);
    const max = limits[type + "PerDay"];
    if (count >= max) {
      setUpgradeReason(reason);
      setShowUpgrade(true);
      return false;
    }
    return true;
  }

  async function loginWithGoogle() {
    try { await signInWithPopup(auth, googleProvider); } catch (e) { console.error(e); }
  }
  async function logout() { await signOut(auth); }

  async function startStage(stageId) {
    setQsLoading(true);
    setQsError(null);
    setSessionQs([]); setQi(0); setAnswered(false); setChosen(null);
    setSessionResults([]); setShowResult(false); setReviewMode(false);
    const result = await fetchSessionQuestions(quizCountry, subcat, stageId);
    if (!result.ok) {
      setQsError(result.error);
    }
    setSessionQs(result.data);
    setQsLoading(false);
  }

  async function answer(i) {
    if (answered || !curQ) return;
    incDailyCount("quiz");
    setAnswered(true);
    setChosen(i);
    const correct = i === curQ.answer;
    const xp = xpForLevel(curQ.level || 1);
    const newResults = [...sessionResults, { correct, chosen: i, q: curQ }];
    setSessionResults(newResults);
    if (correct) {
      const newXP = totalXP + xp;
      const newCorrect = totalCorrect + 1;
      const oldLevel = levelFromXP(totalXP);
      const newLevel = levelFromXP(newXP);
      setTotalXP(newXP);
      setTotalCorrect(newCorrect);
      setXpGain({ amount: xp, key: Date.now() });
      if (newLevel > oldLevel) setLevelUp(newLevel);
      if (user) {
        const today = new Date().toISOString().slice(0, 10);
        const profile = await loadUserProfile(user.uid);
        const lastStudied = profile?.lastStudied || "";
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        let newStreak = streak;
        let streakBonusXP = 0;
        if (lastStudied === today) {
          // already studied today, no change
        } else if (lastStudied === yesterday) {
          newStreak = streak + 1;
          setStreak(newStreak);
          if ([10, 30, 100, 365].includes(newStreak)) {
            streakBonusXP = newStreak >= 365 ? 500 : newStreak >= 100 ? 200 : newStreak >= 30 ? 100 : 50;
          }
        } else {
          newStreak = 1;
          setStreak(1);
        }
        const finalXP = newXP + streakBonusXP;
        const finalLevel = levelFromXP(finalXP);
        if (streakBonusXP > 0) {
          setTotalXP(finalXP);
          if (finalLevel > newLevel) setLevelUp(finalLevel);
          setXpGain({ amount: streakBonusXP, key: Date.now() + 1 });
        }
        await saveUserProfile(user.uid, { totalXP: finalXP, totalCorrect: newCorrect, level: finalLevel, streak: newStreak, lastStudied: today });
        await saveProgress(user.uid, curQ.id, true, xp);
      }
    } else {
      setWrongQuestions(prev => {
        if (prev.find(q => q.id === curQ.id)) return prev;
        return [...prev, curQ];
      });
      if (user) await saveProgress(user.uid, curQ.id, false, 0);
    }
    if (qi === sessionQs.length - 1) {
      setTimeout(() => setShowResult(true), 900);
    }
  }

  function nextQ() { setQi(i => i + 1); setAnswered(false); setChosen(null); }

  function startReview() {
    setReviewMode(true); setReviewIdx(0);
    setReviewAnswered(false); setReviewChosen(null);
  }

  function reviewAnswer(i) {
    if (reviewAnswered) return;
    setReviewAnswered(true); setReviewChosen(i);
  }

  function nextReview() {
    if (reviewIdx < sessionQs.length - 1) {
      setReviewIdx(i => i + 1); setReviewAnswered(false); setReviewChosen(null);
    } else {
      setWrongQuestions([]);
      setReviewMode(false);
      // ホームから起動した場合（sessionResultsが空）はホームに戻る
      if (sessionResults.length > 0) {
        setShowResult(true);
      } else {
        setTab("home");
      }
    }
  }

  async function sendAI() {
    const text = input.trim();
    if (!text || aiLoading) return;
    if (!checkLimit("ai", "ai")) return;
    incDailyCount("ai");
    setInput("");
    const newMsgs = [...messages, { role: "user", text }];
    setMessages(newMsgs);
    setAiLoading(true);
    if (!isPro) setAiRemaining(prev => Math.max(0, prev - 1));
    try {
      const history = newMsgs.slice(0, -1).map(m => ({
        role: m.role === "assistant" ? "model" : "user",
        parts: [{ text: m.text }],
      }));
      const chat = gemini.startChat({ history });
      const result = await chat.sendMessage(text);
      setMessages(prev => [...prev, { role: "assistant", text: result.response.text() }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "An error occurred. Please try again." }]);
    }
    setAiLoading(false);
  }

  function resetToSubcat() {
    setStage(null); setSessionQs([]); setQi(0); setAnswered(false);
    setChosen(null); setSessionResults([]); setShowResult(false);
    setReviewMode(false); setReviewIdx(0);
  }
  function resetToGroup() { resetToSubcat(); setSubcat(null); }
  function resetToCountry() { resetToGroup(); setGroup(null); }

  const curQ        = sessionQs.length ? sessionQs[qi] : null;
  const groupInfo   = GROUPS.find(g => g.id === group);
  const subcatInfo  = groupInfo?.subs.find(s => s.id === subcat);
  const userLevel   = levelFromXP(totalXP);
  const correctCount = sessionResults.filter(r => r.correct).length;
  const incorrects  = sessionResults.filter(r => !r.correct);
  const cleared     = correctCount >= CLEAR_THRESH;

  function finishOnboard() {
    setMyCountries(prev => prev.includes(onboardCountry) ? prev : [onboardCountry, ...prev]);
    setQuizCountry(onboardCountry);
    localStorage.setItem("onboarded", "1");
    setShowOnboard(false);
    setOnboardStep(0);
  }

  const ONBOARD_STEPS = [
    {
      icon: "🌍",
      title: t("Exploridへようこそ", "Welcome to Explorld"),
      body: t("クイズ・ロールプレイ・AI会話で、世界各国のビジネスマナー・生活文化・旅行知識を学ぼう。", "Learn business culture, daily life, and travel tips for countries around the world — through quizzes, roleplay, and AI conversation."),
      btn: t("次へ →", "Next →"),
    },
    {
      features: [
        { ic: "🎯", title: t("クイズ", "Quiz"), desc: t("実際の文化的場面を5問ずつ出題。ステージをクリアして難しいレベルに挑戦。", "5-question sessions on real cultural situations. Clear stages to unlock harder levels.") },
        { ic: "🎭", title: t("ロールプレイ", "Roleplay"), desc: t("リアルな会話を練習。現地語で応答を選択。", "Practice realistic conversations. Choose your response in the local language.") },
        { ic: "🤖", title: t("AIチャット", "AI Chat"), desc: t("文化・マナー・言語について何でも質問。Gemini搭載。", "Ask anything about culture, etiquette, or language. Powered by Gemini.") },
      ],
      btn: t("いいね →", "Sounds good →"),
    },
    {
      title: t("最初の国を選ぼう", "Pick your first country"),
      body: t("学習を始める国を選択。ホーム画面からいつでも追加できます。", "Choose a country to start. You can add more anytime from the home screen."),
      btn: t("始める", "Get Started"),
    },
  ];

  const OnboardModal = () => {
    const step = ONBOARD_STEPS[onboardStep];
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        <div style={{ background: CARD, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 440, padding: "28px 24px 36px", position: "relative" }}>
          {/* progress dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 24 }}>
            {ONBOARD_STEPS.map((_, i) => (
              <div key={i} style={{ width: i === onboardStep ? 20 : 6, height: 6, borderRadius: 3, background: i === onboardStep ? BLUE : BORDER, transition: "all .3s" }} />
            ))}
          </div>

          {onboardStep === 0 && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>{step.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: TEXT1, marginBottom: 12 }}>{step.title}</div>
              <div style={{ fontSize: 14, color: "#666", lineHeight: 1.6, marginBottom: 28 }}>{step.body}</div>
            </div>
          )}

          {onboardStep === 1 && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: TEXT1, marginBottom: 16, textAlign: "center" }}>What you can do</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
                {step.features.map(f => (
                  <div key={f.ic} style={{ display: "flex", alignItems: "flex-start", gap: 12, background: BG, borderRadius: 12, padding: "12px 14px" }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{f.ic}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: TEXT1 }}>{f.title}</div>
                      <div style={{ fontSize: 12, color: TEXT3, marginTop: 2, lineHeight: 1.4 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {onboardStep === 2 && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: TEXT1, marginBottom: 6, textAlign: "center" }}>{step.title}</div>
              <div style={{ fontSize: 13, color: TEXT3, marginBottom: 16, textAlign: "center" }}>{step.body}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 28 }}>
                {AVAILABLE_COUNTRIES.map(co => (
                  <div key={co.c} onClick={() => setOnboardCountry(co.c)}
                    style={{ borderRadius: 12, border: "2px solid " + (onboardCountry === co.c ? BLUE : BORDER), background: onboardCountry === co.c ? BLUE_LIGHT : CARD, padding: "10px 4px", textAlign: "center", cursor: "pointer", transition: "all .15s" }}>
                    <img src={`https://flagcdn.com/w40/${co.c}.png`} alt={co.n} style={{ width: 28, height: 18, objectFit: "cover", borderRadius: 3, marginBottom: 4 }} />
                    <div style={{ fontSize: 10, fontWeight: 600, color: onboardCountry === co.c ? BLUE : TEXT2 }}>{co.n}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => onboardStep < ONBOARD_STEPS.length - 1 ? setOnboardStep(s => s + 1) : finishOnboard()}
            style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 12, padding: 15, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
            {step.btn}
          </button>
          <button onClick={finishOnboard} style={{ display: "block", margin: "12px auto 0", background: "none", border: "none", color: TEXT4, fontSize: 13, cursor: "pointer" }}>
            {t("スキップ", "Skip")}
          </button>
        </div>
      </div>
    );
  };

  const wrap = (children) => (
    <div style={{ maxWidth: 440, margin: "0 auto", fontFamily: "system-ui, -apple-system, sans-serif", background: BG, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`@keyframes xpFloat { 0%{opacity:1;transform:translateY(0) scale(1)} 60%{opacity:1;transform:translateY(-40px) scale(1.1)} 100%{opacity:0;transform:translateY(-70px) scale(0.9)} }`}</style>
      {showUpgrade && <UpgradeModal />}
      {showCountryPick && <CountryPickModal />}

      {/* +XP floating badge animation */}
      {xpGain && (
        <div key={xpGain.key} onAnimationEnd={() => setXpGain(null)}
          style={{ position: "fixed", top: 80, right: 20, zIndex: 300, background: "#f59e0b", color: "#fff", fontWeight: 800, fontSize: 16, borderRadius: 20, padding: "6px 16px", boxShadow: "0 4px 12px rgba(245,158,11,0.5)", animation: "xpFloat 1.8s ease-out forwards", pointerEvents: "none" }}>
          +{xpGain.amount} XP
        </div>
      )}

      {/* Level-up celebration modal */}
      {levelUp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}>
          <div style={{ background: CARD, borderRadius: 20, padding: "32px 24px", width: "100%", maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontSize: 52, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT4, letterSpacing: "0.06em", marginBottom: 6 }}>{t("レベルアップ！", "LEVEL UP!")}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: TEXT1, marginBottom: 4 }}>{LEVEL_TITLES[levelUp]}</div>
            <div style={{ fontSize: 13, color: TEXT3, marginBottom: 24 }}>Lv.{levelUp} {t("に到達しました", "reached!")}</div>
            <button onClick={() => setLevelUp(null)} style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 12, padding: 14, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
              {t("やったー！", "Awesome!")}
            </button>
          </div>
        </div>
      )}

      {showXPModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px" }}>
          <div style={{ background: CARD, borderRadius: 16, padding: "24px 20px", width: "100%", maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEXT4, letterSpacing: "0.06em", marginBottom: 12 }}>YOUR PROGRESS</div>
            <div style={{ fontSize: 40, marginBottom: 4 }}>{LEVEL_TITLES[userLevel].split(" ")[0]}</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>Lv.{userLevel} {LEVEL_TITLES[userLevel].split(" ").slice(1).join(" ")}</div>
            <div style={{ fontSize: 12, color: TEXT3, marginBottom: 16 }}>{t("合計 XP", "Total XP")}: {totalXP}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: TEXT3, marginBottom: 6 }}>
              <span>{t("次のレベルまで", "Next level")}</span><span>{totalXP % 1000} / 1000 XP</span>
            </div>
            <div style={{ height: 6, background: BORDER, borderRadius: 3, marginBottom: 8, overflow: "hidden" }}>
              <div style={{ height: "100%", width: ((totalXP % 1000) / 10) + "%", background: BLUE, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 11, color: TEXT4, marginBottom: 20, textAlign: "left" }}>
              <div>📝 {t("クイズ正解", "Quiz correct")}: 10〜50 XP</div>
              <div>🎭 {t("ロールプレイ正解", "Roleplay correct")}: 5 XP</div>
              <div>🎭 {t("シーン完走ボーナス", "Scene complete bonus")}: +30 XP</div>
              <div>🔥 {t("ストリーク節目（10/30/100日）", "Streak milestone (10/30/100d)")}: 50/100/200 XP</div>
            </div>
            <button onClick={() => setShowXPModal(false)} style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>OK</button>
          </div>
        </div>
      )}
      {showOnboard && <OnboardModal />}

      {/* Learning Goals modal */}
      {showLearningGoals && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: CARD, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 440, padding: "20px 20px 36px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: BORDER, borderRadius: 2, margin: "0 auto 16px" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{t("学習目標・通知", "Learning goals & notifications")}</div>
              <button onClick={() => setShowLearningGoals(false)} style={{ background: "none", border: "none", fontSize: 20, color: TEXT4, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEXT4, letterSpacing: "0.07em", marginBottom: 12 }}>{t("主な目的", "MAIN PURPOSE")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[
                { id: "business",   ic: "💼", label: t("ビジネス", "Business") },
                { id: "life",       ic: "🏠", label: t("海外生活", "Life Abroad") },
                { id: "travel",     ic: "✈️", label: t("旅行", "Travel") },
                { id: "everything", ic: "🌍", label: t("すべて", "Everything") },
              ].map(p => (
                <div key={p.id} onClick={() => setLearningPurpose(p.id)}
                  style={{ border: "2px solid " + (learningPurpose === p.id ? BLUE : BORDER), borderRadius: 12, padding: "14px 12px", cursor: "pointer", background: learningPurpose === p.id ? BLUE_LIGHT : CARD, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22 }}>{p.ic}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: learningPurpose === p.id ? BLUE : TEXT1 }}>{p.label}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEXT4, letterSpacing: "0.07em", marginBottom: 12 }}>{t("デイリーストリーク目標", "DAILY STREAK GOAL")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
              {[
                { val: 7,   ic: "🌱", label: "7d" },
                { val: 30,  ic: "🔥", label: "30d" },
                { val: 100, ic: "💪", label: "100d" },
                { val: 365, ic: "👑", label: "365d" },
              ].map(g => (
                <div key={g.val} onClick={() => setStreakGoal(g.val)}
                  style={{ border: "2px solid " + (streakGoal === g.val ? BLUE : BORDER), borderRadius: 12, padding: "14px 8px", cursor: "pointer", background: streakGoal === g.val ? BLUE_LIGHT : CARD, textAlign: "center" }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{g.ic}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: streakGoal === g.val ? BLUE : TEXT1 }}>{g.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button onClick={() => setShowLearningGoals(false)}
                style={{ background: SUBTLE, color: TEXT2, border: "none", borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>{t("キャンセル", "Cancel")}</button>
              <button onClick={() => { setShowLearningGoals(false); try { localStorage.setItem("streakGoal", String(streakGoal)); localStorage.setItem("learningPurpose", learningPurpose); } catch {} }}
                style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>{t("保存", "Save")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Home Screen modal */}
      {showAddToHome && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: CARD, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 440, padding: "20px 20px 36px" }}>
            <div style={{ width: 36, height: 4, background: BORDER, borderRadius: 2, margin: "0 auto 16px" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{t("ホーム画面に追加", "Add to Home Screen")}</div>
              <button onClick={() => setShowAddToHome(false)} style={{ background: "none", border: "none", fontSize: 20, color: TEXT4, cursor: "pointer" }}>✕</button>
            </div>
            {(appLang === "ja" ? [
              { n: 1, text: "Safariの下部にある共有ボタン（□↑）をタップ" },
              { n: 2, text: "下にスクロールして「ホーム画面に追加」をタップ" },
              { n: 3, text: "右上の「追加」をタップ" },
            ] : [
              { n: 1, text: "Tap the Share button (□↑) at the bottom of Safari" },
              { n: 2, text: "Scroll down and tap \"Add to Home Screen\"" },
              { n: 3, text: "Tap \"Add\" in the top right corner" },
            ]).map(s => (
              <div key={s.n} style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: BLUE, flexShrink: 0 }}>{s.n}</div>
                <div style={{ fontSize: 14, color: TEXT1, lineHeight: 1.5, paddingTop: 6 }}>{s.text}</div>
              </div>
            ))}
            <button onClick={() => setShowAddToHome(false)}
              style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 12, padding: 15, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>{t("わかった", "Got it")}</button>
          </div>
        </div>
      )}

      {/* Terms of Service screen */}
      {showTerms && (() => {
        const isJa = appLang === "ja";
        const LegalDoc = ({ sections, title, gradient, otherLabel, onOther }) => (
          <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 440, height: "100vh", overflowY: "auto", background: BG, zIndex: 200 }}>
            <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "10px 16px", display: "flex", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
              <button onClick={() => setShowTerms(false)} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", fontWeight: 600 }}>← {isJa ? "戻る" : "Back"}</button>
            </div>
            <div style={{ padding: "24px 20px 48px" }}>
              <div style={{ background: gradient, borderRadius: 14, padding: "20px", textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{title}</div>
              </div>
              <div style={{ fontSize: 12, color: TEXT3, marginBottom: 20 }}>{isJa ? "最終更新日：2026年7月13日" : "Last updated: 13 July 2026"}</div>
              {sections.map((sec, i) => (
                <div key={i} style={{ marginBottom: 20 }}>
                  {sec.h && <div style={{ fontSize: 15, fontWeight: 700, color: TEXT1, marginBottom: 8 }}>{isJa ? sec.h.split(" / ")[0] : sec.h.split(" / ")[1]}</div>}
                  <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.8, whiteSpace: "pre-line" }}>{isJa ? sec.ja : sec.en}</div>
                </div>
              ))}
              <div style={{ textAlign: "center", fontSize: 12, color: "#ccc", marginTop: 32 }}>© 2026 Explorid. All rights reserved.</div>
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <button onClick={onOther} style={{ background: "none", border: "none", color: BLUE, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>{otherLabel}</button>
              </div>
            </div>
          </div>
        );
        return <LegalDoc
          title={isJa ? "利用規約" : "Terms of Service"}
          gradient={"linear-gradient(135deg, " + BLUE + ", #7c3aed)"}
          otherLabel={isJa ? "プライバシーポリシー" : "Privacy policy"}
          onOther={() => { setShowTerms(false); setShowPrivacy(true); }}
          sections={[
            { ja: "本利用規約（以下「本規約」）は、Explorid（以下「本サービス」）の利用条件を定めるものです。本サービスをご利用いただく前に、本規約をよくお読みください。本サービスを利用することで、本規約に同意したものとみなします。", en: "These Terms of Service govern your use of Explorid. Please read them carefully before using the Service. By using the Service, you agree to be bound by these Terms." },
            { h: "1. サービスの概要 / 1. Overview", ja: "Exploridは、世界各国のビジネスマナー・生活文化・旅行知識をクイズ形式で学習できるウェブアプリケーションです。本サービスはPWA（Progressive Web App）として提供されます。", en: "Explorid is a web application that lets you learn business etiquette, daily culture, and travel knowledge through quizzes. The Service is provided as a Progressive Web App (PWA)." },
            { h: "2. 利用資格 / 2. Eligibility", ja: "本サービスは13歳以上の方がご利用いただけます。13歳未満の方は保護者の同意を得た上でご利用ください。", en: "The Service is available to users aged 13 and above. Users under 13 must obtain parental consent before use." },
            { h: "3. アカウント / 3. Account", ja: "本サービスはGoogleアカウントによるログインを提供します。アカウント情報の管理はユーザー自身の責任において行ってください。一人のユーザーが複数のアカウントを作成することを禁止します。", en: "The Service uses Google Sign-In for authentication. You are responsible for maintaining the security of your account. Creating multiple accounts per user is prohibited." },
            { h: "4. 料金プランと決済 / 4. Pricing & Payment", ja: "Freeプラン：無料でご利用いただけます。一部機能に制限があります。\nProプラン：月払い・年払いの有料プランです。\n決済はStripeを通じて安全に処理されます。\n有料プランの解約はいつでも可能です。", en: "Free plan: Available at no cost with limited features.\nPaid plans: Monthly or annual subscriptions available.\nPayments are processed securely via Stripe.\nYou may cancel anytime; access continues until the next billing date." },
            { h: "5. 禁止事項 / 5. Prohibited Actions", ja: "・本サービスの不正利用\n・他のユーザーへの迷惑行為\n・コンテンツの無断複製・転載・販売\n・自動化ツールやボットによるアクセス\n・法令または公序良俗に違反する行為", en: "· Unauthorized use or misuse of the Service\n· Harassment of other users\n· Reproducing, redistributing, or selling content without permission\n· Access via automated tools or bots\n· Any act violating laws or public order" },
            { h: "6. 知的財産権 / 6. Intellectual Property", ja: "本サービス上のコンテンツの著作権は、Exploridまたはコンテンツ提供者に帰属します。ユーザーは個人的な学習目的のみで利用できます。", en: "All content on the Service is owned by Explorid or its content providers. Users may use content for personal learning purposes only." },
            { h: "7. 免責事項 / 7. Disclaimer", ja: "本サービスは「現状有姿」で提供されます。コンテンツの正確性・完全性については保証しません。", en: "The Service is provided \"as is\" without warranty of any kind. We do not guarantee the accuracy or completeness of content." },
            { h: "8. 準拠法・管轄 / 8. Governing Law", ja: "本規約は日本法に準拠します。紛争については東京地方裁判所を管轄とします。", en: "These Terms are governed by Japanese law. Disputes shall be subject to the jurisdiction of the Tokyo District Court." },
            { h: "9. お問い合わせ / 9. Contact", ja: "サービス名：Explorid\nウェブサイト：https://explorid.com\nメール：support@explorid.com", en: "Service: Explorid\nWebsite: https://explorid.com\nEmail: support@explorid.com" },
          ]}
        />;
      })()}

      {/* Privacy Policy screen */}
      {showPrivacy && (() => {
        const isJa = appLang === "ja";
        return (
          <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 440, height: "100vh", overflowY: "auto", background: BG, zIndex: 200 }}>
            <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "10px 16px", display: "flex", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
              <button onClick={() => setShowPrivacy(false)} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", fontWeight: 600 }}>← {isJa ? "戻る" : "Back"}</button>
            </div>
            <div style={{ padding: "24px 20px 48px" }}>
              <div style={{ background: "linear-gradient(135deg, #7c3aed, #db2777)", borderRadius: 14, padding: "20px", textAlign: "center", marginBottom: 24 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{isJa ? "プライバシーポリシー" : "Privacy Policy"}</div>
              </div>
              <div style={{ fontSize: 12, color: TEXT3, marginBottom: 20 }}>{isJa ? "最終更新日：2026年7月13日" : "Last updated: 13 July 2026"}</div>
              {[
                { ja: "Exploridは、ユーザーのプライバシーを尊重し、個人情報の保護に努めます。本ポリシーは、本サービスにおける個人情報の取り扱いを説明します。", en: "Explorid is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your personal information." },
                { h: "1. 収集する情報 / 1. Information We Collect", ja: "・アカウント情報：氏名・メールアドレス・プロフィール画像\n・学習データ：クイズ回答履歴、正解数、ストリーク、レベル\n・利用データ：アクセスログ、使用端末、IPアドレス\n・決済情報：Stripeが安全に処理（カード情報は保持しません）", en: "· Account info: Name, email address, profile photo\n· Learning data: Quiz history, correct answers, streaks, levels\n· Usage data: Access logs, device type, IP address\n· Payment info: Processed securely by Stripe (we do not store card data)" },
                { h: "2. 情報の利用目的 / 2. How We Use Your Information", ja: "・本サービスの提供・改善・パーソナライズ\n・学習進捗の記録・管理\n・ユーザーサポートの提供\n・法令に基づく対応", en: "· Providing, improving, and personalising the Service\n· Tracking and managing your learning progress\n· Providing user support\n· Complying with legal obligations" },
                { h: "3. 第三者提供 / 3. Third-Party Sharing", ja: "以下の場合を除き、個人情報を第三者に提供しません。\n・ユーザーの同意がある場合\n・法令に基づく場合\n・人の生命・財産の保護に必要な場合", en: "We do not share your personal information with third parties except:\n· With your consent\n· As required by law\n· To protect the life or property of persons" },
                { h: "4. 利用する外部サービス / 4. Third-Party Services", ja: "・Google Firebase：認証・データベース・ホスティング\n・Stripe：決済処理\n・Anthropic Claude API：AI機能の提供", en: "· Google Firebase: Authentication, database, hosting\n· Stripe: Payment processing\n· Anthropic Claude API: AI features" },
                { h: "5. データの安全管理 / 5. Data Security", ja: "個人情報はGoogle Firebaseの安全なサーバーに保管されます。不正アクセス・紛失・破壊・改ざんを防止するための適切な措置を講じます。", en: "Personal data is stored on Google Firebase's secure servers. We implement appropriate measures to protect against unauthorised access, loss, or alteration." },
                { h: "6. GDPRおよびUK GDPR / 6. GDPR & UK GDPR", ja: "EEAおよび英国のユーザーは以下の権利を有します。\n・個人データへのアクセス権\n・訂正権\n・削除権（忘れられる権利）\n・処理制限権\n・データポータビリティの権利", en: "EEA and UK users have the following rights:\n· Right of access\n· Right to rectification\n· Right to erasure (right to be forgotten)\n· Right to restriction of processing\n· Right to data portability" },
                { h: "7. お問い合わせ / 7. Contact", ja: "サービス名：Explorid\nウェブサイト：https://explorid.com\nメール：privacy@explorid.com", en: "Service: Explorid\nWebsite: https://explorid.com\nEmail: privacy@explorid.com" },
              ].map((sec, i) => (
                <div key={i} style={{ marginBottom: 20 }}>
                  {sec.h && <div style={{ fontSize: 15, fontWeight: 700, color: TEXT1, marginBottom: 8 }}>{isJa ? sec.h.split(" / ")[0] : sec.h.split(" / ")[1]}</div>}
                  <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.8, whiteSpace: "pre-line" }}>{isJa ? sec.ja : sec.en}</div>
                </div>
              ))}
              <div style={{ textAlign: "center", fontSize: 12, color: "#ccc", marginTop: 32 }}>© 2026 Explorid. All rights reserved.</div>
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <button onClick={() => { setShowPrivacy(false); setShowTerms(true); }} style={{ background: "none", border: "none", color: BLUE, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>{isJa ? "利用規約" : "Terms of service"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showProfile && (
        <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 440, height: "100vh", overflowY: "auto", background: BG, zIndex: 150 }}>
          <AppHeader backLabel={t("ホーム", "Home")} onBack={() => { setShowProfile(false); setTab("home"); }} />
          <div style={{ padding: "20px 16px" }}>
            {/* Avatar + info */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, background: CARD, border: "1px solid " + BORDER, borderRadius: 14, padding: "16px" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 22, fontWeight: 700, flexShrink: 0 }}>
                {user?.displayName?.[0] || "?"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT1 }}>{user?.displayName || "Guest"}</div>
                <div style={{ fontSize: 11, color: TEXT3, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || ""}</div>
              </div>
              <div style={{ background: isPro ? BLUE : BORDER, color: isPro ? "#fff" : TEXT3, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{isPro ? "Pro" : "Free"}</div>
            </div>
            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
              {[
                { ic: "🔥", val: streak, label: t("連続日数", "Day streak") },
                { ic: "✓", val: correctCount, label: t("正解数", "Correct") },
                { ic: "📝", val: sessionQs.length, label: t("問題数", "Questions") },
              ].map(s => (
                <div key={s.label} style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 10, padding: "10px 6px", textAlign: "center" }}>
                  <div style={{ fontSize: 16, marginBottom: 2 }}>{s.ic}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: TEXT1 }}>{s.val}</div>
                  <div style={{ fontSize: 9, color: TEXT4, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* Settings */}
            <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid " + BORDER }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: TEXT4, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>{t("英語名（任意）", "ENGLISH NAME")}</div>
                <input placeholder={t("例：Yoshi", "e.g. Yoshi")} style={{ width: "100%", border: "1px solid " + BORDER, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: CARD, color: TEXT1 }} />
              </div>
              <div style={{ padding: "12px 14px", borderBottom: "1px solid " + BORDER, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, color: TEXT1, display: "flex", alignItems: "center", gap: 8 }}>🌐 {t("言語", "Language")}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <div onClick={() => setLang("ja")} style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: appLang === "ja" ? BLUE : SUBTLE, color: appLang === "ja" ? "#fff" : TEXT3, cursor: "pointer" }}>日本語</div>
                  <div onClick={() => setLang("en")} style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: appLang === "en" ? BLUE : SUBTLE, color: appLang === "en" ? "#fff" : TEXT3, cursor: "pointer" }}>English</div>
                </div>
              </div>
              <div style={{ padding: "12px 14px", borderBottom: "1px solid " + BORDER, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, color: TEXT1, display: "flex", alignItems: "center", gap: 8 }}>🌙 {t("ダークモード", "Dark mode")}</div>
                <div onClick={toggleDark} style={{ width: 40, height: 22, borderRadius: 11, background: darkMode ? BLUE : BORDER, cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: darkMode ? 20 : 2, boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                </div>
              </div>
              <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, color: TEXT1, display: "flex", alignItems: "center", gap: 8 }}>🔔 {t("デイリーリマインダー", "Daily Reminder")}</div>
                <button onClick={requestNotification} style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{t("有効化", "Enable")}</button>
              </div>
            </div>
            <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
              {[
                { label: "🎯 " + t("学習目標・通知", "Learning goals & notifications"), action: () => setShowLearningGoals(true) },
                { label: "📱 " + t("ホーム画面に追加", "Add to home screen"),            action: () => setShowAddToHome(true) },
                { label: "📄 " + t("利用規約", "Terms of service"),              action: () => setShowTerms(true) },
                { label: "🔒 " + t("プライバシーポリシー", "Privacy policy"),               action: () => setShowPrivacy(true) },
              ].map((item, i, arr) => (
                <div key={i} onClick={item.action}
                  style={{ padding: "13px 14px", borderBottom: i < arr.length - 1 ? "1px solid " + BORDER : "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
                  <div style={{ fontSize: 13, color: TEXT1 }}>{item.label}</div>
                  <span style={{ color: "#bbb", fontSize: 16 }}>›</span>
                </div>
              ))}
            </div>
            {/* Upgrade */}
            <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: "12px 14px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT1 }}>{isPro ? "Early Access" : "Free"}</div>
                <div style={{ fontSize: 11, color: TEXT3 }}>{t("クイズ・カードのみ", "Quizzes & cards only")}</div>
              </div>
              {!isPro && <button onClick={() => setShowUpgrade(true)} style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("アップグレード", "Upgrade")}</button>}
            </div>
            <div style={{ textAlign: "center", fontSize: 11, color: TEXT4, marginBottom: 12 }}>Explorid v1.0.0</div>
            {user
              ? <button onClick={() => { logout(); setShowProfile(false); }} style={{ width: "100%", background: "none", border: "none", color: "#ef4444", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "10px 0" }}>{t("ログアウト", "Log out")}</button>
              : <button onClick={() => { loginWithGoogle(); setShowProfile(false); }} style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{t("Googleでログイン", "Sign in with Google")}</button>
            }
          </div>
        </div>
      )}
      {children}
    </div>
  );

  // ── Sub-components ─────────────────────────────────────────────

  // Common right-side header controls (appears on ALL non-home screens)
  const requestNotification = () => {
    if ("Notification" in window) {
      Notification.requestPermission().then(p => {
        if (p === "granted") alert(appLang === "ja" ? "通知が許可されました！" : "Notifications enabled!");
        else alert(appLang === "ja" ? "通知がブロックされています。ブラウザ設定をご確認ください。" : "Notifications blocked. Please check your browser settings.");
      });
    } else {
      alert(appLang === "ja" ? "このブラウザは通知をサポートしていません。" : "Notifications are not supported in this browser.");
    }
  };

  const HeaderRight = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2, background: darkMode ? "#2a2010" : "#fff8e1", borderRadius: 20, padding: "3px 9px" }}>
        <span style={{ fontSize: 13 }}>🔥</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: darkMode ? "#f59e0b" : "#d97706" }}>{streak}</span>
      </div>
      <div onClick={requestNotification} style={{ width: 30, height: 30, borderRadius: "50%", background: darkMode ? "#2a2010" : "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer" }}>🔔</div>
      {user
        ? <div onClick={() => setShowProfile(true)} style={{ width: 30, height: 30, borderRadius: "50%", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            {user.displayName?.[0] || "U"}
          </div>
        : <div onClick={() => setShowProfile(true)} style={{ width: 30, height: 30, borderRadius: "50%", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer" }}>✈️</div>
      }
    </div>
  );

  // Standard header for non-home screens: left = home or back, right = controls
  const AppHeader = ({ backLabel, onBack }) => (
    <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {backLabel
          ? <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: 0, fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}>← {backLabel}</button>
          : <button onClick={() => { setShowProfile(false); setTab("home"); }} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: 0, fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}>← {t("ホーム", "Home")}</button>
        }
      </div>
      <HeaderRight />
    </div>
  );

  const BackBtn = ({ label = "Back", onClick }) => (
    <button onClick={onClick} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: 0, fontWeight: 600, display: "flex", alignItems: "center", gap: 2 }}>
      ← {label}
    </button>
  );


  const CountryPickModal = () => {
    const searchLower = countrySearch.toLowerCase();
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        <div style={{ background: CARD, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 440, maxHeight: "85vh", display: "flex", flexDirection: "column", position: "relative" }}>
          <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid " + BORDER }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{t("国を選択", "Choose a country")}</div>
              <button onClick={() => { setShowCountryPick(false); setCountrySearch(""); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: TEXT4 }}>✕</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: SUBTLE, borderRadius: 10, padding: "8px 12px" }}>
              <span style={{ fontSize: 16 }}>🔍</span>
              <input value={countrySearch} onChange={e => setCountrySearch(e.target.value)}
                placeholder={t("国を検索...", "Search country...")}
                style={{ border: "none", background: "none", flex: 1, fontSize: 14, outline: "none", fontFamily: "inherit" }} />
            </div>
          </div>
          <div style={{ overflowY: "auto", padding: "12px 20px 24px" }}>
            {COUNTRY_GROUPS.map(grp => {
              const filtered = grp.countries.filter(c => c.n.toLowerCase().includes(searchLower));
              if (filtered.length === 0) return null;
              return (
                <div key={grp.label} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TEXT4, letterSpacing: "0.06em", marginBottom: 10 }}>{grp.label}</div>
                  {grp.soon ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
                      {filtered.map(c => (
                        <div key={c.c} style={{ textAlign: "center", opacity: 0.5 }}>
                          <img src={`https://flagcdn.com/w40/${c.c}.png`} alt={c.n} style={{ width: 30, height: "auto", marginBottom: 2, display: "block", margin: "0 auto 2px" }} />
                          <div style={{ fontSize: 10, color: TEXT3 }}>{c.n}</div>
                          <div style={{ fontSize: 9, color: BLUE, fontWeight: 600 }}>Soon</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {filtered.map(c => {
                        const isAdded = myCountries.includes(c.c);
                        return (
                          <div key={c.c} onClick={() => {
                            if (!isAdded) setMyCountries(prev => [...prev, c.c]);
                            setQuizCountry(c.c); setShowCountryPick(false); setCountrySearch("");
                          }}
                            style={{ background: isAdded ? BLUE_LIGHT : CARD, border: isAdded ? "1.5px solid " + BLUE : "1px solid " + BORDER, borderRadius: 12, padding: "14px", cursor: "pointer", textAlign: "center" }}>
                            <img src={`https://flagcdn.com/w40/${c.c}.png`} alt={c.n} style={{ width: 44, height: "auto", marginBottom: 6, display: "block", margin: "0 auto 6px" }} />
                            <div style={{ fontSize: 14, fontWeight: 600, color: TEXT1 }}>{c.n}</div>
                            <div style={{ fontSize: 11, color: GREEN, fontWeight: 600, marginTop: 2 }}>Free</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const UpgradeModal = () => {
    const reasonMsg = {
      stage:    { ja: "Stage 3以降はVoyager以上で解放されます", en: "Stage 3+ unlocks with Voyager or above" },
      quiz:     { ja: "本日のクイズ上限（20問）に達しました", en: "You've reached today's quiz limit (20 questions)" },
      roleplay: { ja: "本日のロールプレイ上限（1シーン）に達しました", en: "You've reached today's roleplay limit (1 scene)" },
      ai:       { ja: "本日のAIチャット上限に達しました", en: "You've reached today's AI chat limit" },
    }[upgradeReason] || { ja: "プランをアップグレードして続けましょう", en: "Upgrade your plan to continue" };

    const rows = [
      { label: t("クイズ", "Quiz"),           free: "Stage 1-2 / 20問/日", tra: t("Stage 1-3・無制限", "Stage 1-3 · Unlimited"), amb: t("全Stage・無制限", "All stages · Unlimited") },
      { label: t("ロールプレイ", "Roleplay"),  free: t("基本・1回/日", "Basic · 1/day"),   tra: t("基本・無制限", "Basic · Unlimited"), amb: t("全シーン・無制限", "All scenes · Unlimited") },
      { label: t("AIチャット", "AI Chat"),     free: "3回/日",              tra: "20回/日",  amb: "60回/日" },
      { label: t("カ国数", "Countries"),       free: "2カ国",               tra: "5カ国",    amb: t("無制限", "Unlimited") },
      { label: t("弱点保存", "Weak points"),   free: t("当日のみ", "Today only"), tra: "30日間", amb: t("無制限", "Unlimited") },
    ];
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        <div style={{ background: CARD, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 440, padding: "20px 20px 36px", maxHeight: "92vh", overflowY: "auto" }}>
          <div style={{ width: 36, height: 4, background: BORDER, borderRadius: 2, margin: "0 auto 16px" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{t("プランをアップグレード", "Upgrade your plan")}</div>
            <button onClick={() => setShowUpgrade(false)} style={{ background: "none", border: "none", fontSize: 22, color: TEXT4, cursor: "pointer" }}>✕</button>
          </div>
          <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#7a5800", marginBottom: 16 }}>
            ⚡ {t(reasonMsg.ja, reasonMsg.en)}
          </div>

          {/* Billing cycle toggle */}
          <div style={{ display: "flex", background: SUBTLE, borderRadius: 20, padding: 3, marginBottom: 14, gap: 2 }}>
            {[["monthly", t("月額", "Monthly")], ["yearly", t("年額 -33%", "Annual -33%")]].map(([cycle, label]) => (
              <div key={cycle} onClick={() => setBillingCycle(cycle)}
                style={{ flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 18, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  background: billingCycle === cycle ? BLUE : "transparent",
                  color: billingCycle === cycle ? "#fff" : TEXT3 }}>
                {label}
              </div>
            ))}
          </div>

          {/* Plan cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { id: "traveller",  name: "🌍 Traveller",  mo: "£2.99", yr: "£23.99", color: "#16a34a", light: "#f0fdf4" },
              { id: "ambassador", name: "🏛️ Ambassador", mo: "£3.99", yr: "£31.99", color: "#7c3aed", light: "#f3e8ff" },
            ].map(p => {
              const isOwner = user?.email === OWNER_EMAIL;
              const isCurrent = plan === p.id;
              const price = billingCycle === "yearly" ? p.yr : p.mo;
              const perLabel = billingCycle === "yearly" ? t("/年", "/yr") : t("/月", "/mo");
              return (
                <div key={p.id} style={{ border: "2px solid " + (isCurrent ? p.color : BORDER), borderRadius: 14, padding: "14px 12px", textAlign: "center", background: isCurrent ? p.light : CARD }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: p.color, marginBottom: 4 }}>{p.name}</div>
                  {isCurrent && <div style={{ fontSize: 10, background: p.color, color: "#fff", borderRadius: 10, padding: "2px 6px", marginBottom: 6, display: "inline-block" }}>{t("現在のプラン", "Current plan")}</div>}
                  <div style={{ fontSize: 22, fontWeight: 800, color: p.color }}>{price}</div>
                  <div style={{ fontSize: 10, color: TEXT3, marginBottom: 10 }}>{perLabel}</div>
                  <button onClick={() => {
                    if (isOwner) {
                      setPlan(p.id); saveUserProfile(user.uid, { plan: p.id });
                    }
                    setShowUpgrade(false);
                  }}
                    style={{ width: "100%", background: isCurrent ? SUBTLE : p.color, color: isCurrent ? TEXT3 : "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {isCurrent ? t("利用中", "Current") : isOwner ? t("切替（テスト）", "Switch (Test)") : t("選択", "Select")}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Feature comparison */}
          <div style={{ border: "1px solid " + BORDER, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", background: SUBTLE }}>
              {[t("機能", "Feature"), t("無料", "Free"), "🌍 Traveller", "🏛️ Ambassador"].map((h, i) => (
                <div key={i} style={{ padding: "8px 6px", fontSize: 10, fontWeight: 700, color: i === 2 ? "#16a34a" : i === 3 ? "#7c3aed" : TEXT3, textAlign: i === 0 ? "left" : "center" }}>{h}</div>
              ))}
            </div>
            {rows.map((row, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", borderTop: "1px solid " + BORDER }}>
                <div style={{ padding: "9px 6px", fontSize: 11, color: TEXT2 }}>{row.label}</div>
                <div style={{ padding: "9px 4px", fontSize: 10, color: TEXT3, textAlign: "center" }}>{row.free}</div>
                <div style={{ padding: "9px 4px", fontSize: 10, color: "#16a34a", fontWeight: 600, textAlign: "center", background: "#f0fdf4" }}>{row.tra}</div>
                <div style={{ padding: "9px 4px", fontSize: 10, color: "#7c3aed", fontWeight: 600, textAlign: "center", background: "#f3e8ff" }}>{row.amb}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", fontSize: 11, color: TEXT4 }}>{t("いつでもキャンセル可 · Stripeで安全決済", "Cancel anytime · Secure payment via Stripe")}</div>
        </div>
      </div>
    );
  };

  // ── HOME ────────────────────────────────────────────────────────

  // Checklist screen
  if (showChecklist) {
    const cc = quizCountry || "gb";
    const cl = PREP_CHECKLISTS[cc] || PREP_CHECKLISTS["gb"];
    const tabs = cl.tabs || [];
    const safeTabIdx = Math.min(clTabIdx, tabs.length - 1);
    const currentTab = tabs[safeTabIdx] || tabs[0];
    const allItems = currentTab ? currentTab.categories.flatMap(cat => cat.items) : [];
    const key = (item) => cc + ":" + item;
    const toggleItem = (item) => {
      const k = key(item);
      const updated = { ...checkedItems, [k]: !checkedItems[k] };
      setCheckedItems(updated);
      try { localStorage.setItem("checkedItems", JSON.stringify(updated)); } catch {}
    };
    const checkedCount = allItems.filter(it => checkedItems[key(it)]).length;
    const pct = allItems.length > 0 ? Math.round(checkedCount / allItems.length * 100) : 0;

    return wrap(<>
      <AppHeader backLabel={t("ホーム", "Home")} onBack={() => { setShowChecklist(false); setClTabIdx(0); }} />
      {/* Tab selector */}
      {tabs.length > 1 && (
        <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "8px 16px", display: "flex", gap: 6 }}>
          {tabs.map((tab, i) => {
            const tabItems = tab.categories.flatMap(c => c.items);
            const tabChecked = tabItems.filter(it => checkedItems[key(it)]).length;
            const tabPct = tabItems.length > 0 ? Math.round(tabChecked / tabItems.length * 100) : 0;
            return (
              <div key={tab.id} onClick={() => setClTabIdx(i)}
                style={{ flex: 1, padding: "7px 6px", borderRadius: 20, background: safeTabIdx === i ? BLUE : SUBTLE, color: safeTabIdx === i ? "#fff" : TEXT2, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
                <span>{tab.label}</span>
                <span style={{ fontSize: 10, opacity: 0.8 }}>{tabPct}%</span>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ padding: "12px 16px 32px" }}>
        {/* Progress */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontSize: 12, color: TEXT4 }}>{checkedCount}/{allItems.length} {t("完了", "done")}</div>
          <div style={{ fontSize: 12, color: BLUE, fontWeight: 700 }}>{pct}%</div>
        </div>
        <div style={{ height: 4, background: BORDER, borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ height: "100%", width: pct + "%", background: GREEN, borderRadius: 2, transition: "width .3s" }} />
        </div>
        {/* Categories */}
        {currentTab && currentTab.categories.map((cat, ci) => (
          <div key={ci} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 17 }}>{cat.emoji}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: TEXT1 }}>{cat.label}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {cat.items.map((item, i) => {
                const checked = checkedItems[key(item)];
                return (
                  <div key={i} onClick={() => toggleItem(item)}
                    style={{ background: CARD, border: "1px solid " + (checked ? GREEN : BORDER), borderRadius: 10, padding: "11px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, border: "2px solid " + (checked ? GREEN : BORDER), background: checked ? GREEN : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {checked && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <div style={{ fontSize: 13, color: checked ? TEXT4 : TEXT1, textDecoration: checked ? "line-through" : "none", lineHeight: 1.4 }}>{item}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>);
  }

  if (tab === "home") {
    const cl = PREP_CHECKLISTS[quizCountry || "gb"] || PREP_CHECKLISTS["gb"];
    const clKey = (item) => (quizCountry || "gb") + ":" + item;
    const clAllItems = (cl.tabs || []).flatMap(t => t.categories.flatMap(c => c.items));
    const clChecked = clAllItems.filter(it => checkedItems[clKey(it)]).length;
    const weakCount = wrongQuestions.length;

    return wrap(<>
      {/* Header */}
      <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #4f6ef7, #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15, fontWeight: 900 }}>Q</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: TEXT1, letterSpacing: "-0.5px" }}>Explorid</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3, background: darkMode ? "#2a2010" : "#fff8e1", borderRadius: 20, padding: "4px 10px" }}>
            <span style={{ fontSize: 14 }}>🔥</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: darkMode ? "#f59e0b" : "#d97706" }}>{streak}</span>
          </div>
          <div onClick={requestNotification} style={{ width: 32, height: 32, borderRadius: "50%", background: darkMode ? "#2a2010" : "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, cursor: "pointer" }}>🔔</div>
          {user
            ? <div onClick={() => setShowProfile(true)} style={{ width: 32, height: 32, borderRadius: "50%", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {user.displayName?.[0] || "U"}
              </div>
            : <div onClick={() => setShowProfile(true)} style={{ width: 32, height: 32, borderRadius: "50%", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, cursor: "pointer" }}>✈️</div>
          }
        </div>
      </div>

      <div style={{ padding: "12px 16px 0" }}>
        {/* Country chips — tap to select active country, no quiz navigation */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12 }}>
          {myCountries.map(cc => {
            const co = AVAILABLE_COUNTRIES.find(x => x.c === cc);
            if (!co) return null;
            const isActive = quizCountry === cc;
            return (
              <div key={cc} onClick={() => setQuizCountry(cc)}
                style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer" }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", border: isActive ? "3px solid " + BLUE : "2px solid " + BORDER, overflow: "hidden", background: CARD, boxShadow: isActive ? "0 0 0 2px " + BLUE_LIGHT : "none" }}>
                  <img src={`https://flagcdn.com/w80/${co.c}.png`} alt={co.n} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ fontSize: 10, color: isActive ? BLUE : TEXT3, fontWeight: isActive ? 700 : 400 }}>Lv.{userLevel}</div>
                <div style={{ fontSize: 9, color: TEXT4 }}>{co.n}</div>
              </div>
            );
          })}
          <div onClick={() => setShowCountryPick(true)} style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", border: "2px dashed " + BORDER, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, background: SUBTLE, color: TEXT4 }}>+</div>
            <div style={{ fontSize: 10, color: TEXT4 }}>{t("追加", "Add")}</div>
          </div>
        </div>

        {/* XP / level row */}
        <div onClick={() => setShowXPModal(true)} style={{ background: darkMode ? "#1e1a0c" : "#fff8e1", border: "1px solid " + (darkMode ? "#3a3010" : "#ffe082"), borderRadius: 10, padding: "10px 14px", marginBottom: 12, cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 14 }}>🔥{streak}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: darkMode ? "#f59e0b" : "#7a5800" }}>{LEVEL_TITLES[userLevel]}</span>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: darkMode ? "#f59e0b" : "#7a5800" }}>{totalXP % 1000} XP</span>
            <span style={{ fontSize: 11, color: TEXT4 }}>/ 1000</span>
            <span style={{ color: TEXT4, fontSize: 16 }}>›</span>
          </div>
          <div style={{ height: 5, background: darkMode ? "#3a3010" : "#ffe082", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: Math.min(100, ((totalXP % 1000) / 1000) * 100) + "%", background: "#f59e0b", borderRadius: 3, transition: "width .4s" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span style={{ fontSize: 10, color: TEXT4 }}>{t(`✓ ${totalCorrect} 正解`, `✓ ${totalCorrect} correct`)}</span>
            <span style={{ fontSize: 10, color: TEXT4 }}>{t(`次のレベルまで ${1000 - (totalXP % 1000)} XP`, `${1000 - (totalXP % 1000)} XP to next level`)}</span>
          </div>
        </div>

        {/* Developer plan switcher (owner only) */}
        {user?.email === OWNER_EMAIL && (
          <div style={{ background: darkMode ? "#1a0a2e" : "#f3e8ff", border: "1px solid #c4b5fd", borderRadius: 10, padding: "8px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed" }}>🔧 Dev:</span>
            {["free", "traveller", "ambassador"].map(p => (
              <div key={p} onClick={() => { setPlan(p); saveUserProfile(user.uid, { plan: p }); }}
                style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12, cursor: "pointer", fontWeight: plan === p ? 800 : 400,
                  background: plan === p ? "#7c3aed" : "transparent", color: plan === p ? "#fff" : "#7c3aed" }}>
                {p}
              </div>
            ))}
          </div>
        )}

        {/* Daily Challenge */}
        <div onClick={() => { setShowProfile(false); setQuizCountry(quizCountry || "gb"); setGroup(null); setSubcat(null); setStage(null); setTab("quiz"); }}
          style={{ background: CARD, border: "1px solid " + BORDER, borderLeft: "4px solid " + BLUE, borderRadius: 12, padding: "14px 16px", marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: darkMode ? "#1e1a0c" : "#fff8e1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>⭐</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT1 }}>{t("デイリーチャレンジ", "Daily Challenge")}</div>
            <div style={{ fontSize: 12, color: TEXT3, marginTop: 2 }}>{t("ビジネス文化 · 5問", "Business Culture · 5 questions")}</div>
          </div>
          <span style={{ color: "#bbb", fontSize: 18 }}>›</span>
        </div>

        {/* Review Weak Points */}
        <div onClick={() => { if (weakCount > 0) { setTab("quiz"); setSessionQs(wrongQuestions); setQi(0); setAnswered(false); setChosen(null); setSessionResults([]); setShowResult(false); setReviewMode(true); setReviewIdx(0); setReviewAnswered(false); setReviewChosen(null); } }}
          style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: "14px 16px", marginBottom: 14, cursor: weakCount > 0 ? "pointer" : "default", display: "flex", alignItems: "center", gap: 12, opacity: weakCount > 0 ? 1 : 0.5 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: darkMode ? "#1e1208" : "#fff0e6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📋</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT1 }}>{t("弱点レビュー", "Review Weak Points")}</div>
            <div style={{ fontSize: 12, color: TEXT3, marginTop: 2 }}>{weakCount > 0 ? t(`${weakCount}問保存済み`, `${weakCount} questions saved`) : t("まだ弱点がありません", "No weak points yet")}</div>
          </div>
          <span style={{ color: "#bbb", fontSize: 18 }}>›</span>
        </div>

        {/* Learn section */}
        <div style={{ fontSize: 11, color: TEXT4, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>{t("学ぶ", "LEARN")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
          {[
            { ic: "🧠", label: t("クイズ", "Quiz"), sub: t(`ステージ1 · Lv.${userLevel}`, `Stage 1 · Lv.${userLevel}`), bg: "linear-gradient(135deg, #4f6ef7, #6366f1)", tab: "quiz" },
            { ic: "🎭", label: t("ロールプレイ", "Roleplay"), sub: t(`${SCENE_MENU.filter(s => s.available !== false && SCENE_DIALOGUES[s.id] && (!s.countries?.length || s.countries.includes(quizCountry))).length}個解放済み`, `${SCENE_MENU.filter(s => s.available !== false && SCENE_DIALOGUES[s.id] && (!s.countries?.length || s.countries.includes(quizCountry))).length} unlocked`), bg: "linear-gradient(135deg, #22c55e, #16a34a)", tab: "roleplay" },
            { ic: "🤖", label: t("AIチャット", "AI Chat"), sub: t("何でも聞いて", "Ask anything"), bg: "linear-gradient(135deg, #f97316, #ef4444)", tab: "ai" },
          ].map(item => (
            <div key={item.tab} onClick={() => {
              setShowProfile(false);
              if (item.tab === "quiz") {
                if (!quizCountry) setQuizCountry(myCountries[0] || "gb");
                setGroup(null); setSubcat(null); setStage(null);
                setShowResult(false); setReviewMode(false);
              }
              setTab(item.tab);
            }}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}>
              <div style={{ width: 64, height: 64, borderRadius: 18, background: item.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, marginBottom: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                {item.ic}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT1 }}>{item.label}</div>
              <div style={{ fontSize: 11, color: TEXT4, marginTop: 2 }}>{item.sub}</div>
            </div>
          ))}
        </div>

        {/* Checklist */}
        <div onClick={() => setShowChecklist(true)}
          style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: GREEN, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>✓</div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: TEXT1 }}>{cl.label}</div>
          <div style={{ fontSize: 12, color: TEXT4 }}>{clChecked}/{clAllItems.length}</div>
          <span style={{ color: "#bbb", fontSize: 16 }}>›</span>
        </div>
      </div>
    </>);
  }

  // ── QUIZ ────────────────────────────────────────────────────────

  if (tab === "quiz") {
    // Review mode
    if (reviewMode) {
      // sessionQs holds the questions to review:
      // - from result screen: full session questions (we show only incorrect ones by index)
      // - from home screen: wrongQuestions loaded directly into sessionQs
      const reviewList = sessionResults.length > 0
        ? sessionResults.filter(r => !r.correct).map(r => r.q)
        : sessionQs;
      const rq = reviewList[reviewIdx];
      const totalWrong = reviewList.length;
      return wrap(<>
        <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
          <BackBtn label={t("結果に戻る", "Back to results")} onClick={() => { setReviewMode(false); setShowResult(true); }} />
          <HeaderRight />
        </div>
        <div style={{ padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>📖</span>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{t("レビュー", "Review")}</div>
          </div>
          <div style={{ fontSize: 12, color: TEXT4, marginBottom: 16 }}>{t(`不正解を再挑戦 (${reviewIdx + 1}/${totalWrong})`, `Retry incorrect answers (${reviewIdx + 1}/${totalWrong})`)}</div>
          <div style={{ height: 4, background: BORDER, borderRadius: 2, marginBottom: 16, overflow: "hidden" }}>
            <div style={{ height: "100%", width: ((reviewIdx + 1) / totalWrong * 100) + "%", background: "#f97316", borderRadius: 2, transition: "width .3s" }} />
          </div>
          {rq && (
            <div style={{ background: darkMode ? "#2a2010" : "#fff8e1", border: "1px solid " + (darkMode ? "#3a3010" : "#ffe082"), borderRadius: 8, padding: "6px 12px", fontSize: 11, color: darkMode ? "#f59e0b" : "#7a5800", fontWeight: 600, marginBottom: 12 }}>
              ⚠️ {t("前回不正解", "Previously incorrect")}
            </div>
          )}
          {rq && (
            <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: TEXT1, marginBottom: 14, lineHeight: 1.55 }}>{rq.question}</div>
              <div style={{ display: "grid", gap: 8 }}>
                {(rq.choices || []).map((ch, i) => {
                  let bg = SUBTLE, border = "1px solid " + BORDER, color = TEXT1;
                  if (reviewAnswered) {
                    if (i === rq.answer) { bg = darkMode ? "#14532d" : "#dcfce7"; border = "1.5px solid " + GREEN; color = darkMode ? "#86efac" : "#15803d"; }
                    else if (i === reviewChosen) { bg = darkMode ? "#450a0a" : "#fee2e2"; border = "1.5px solid " + RED; color = darkMode ? "#fca5a5" : "#dc2626"; }
                  }
                  return (
                    <button key={i} onClick={() => reviewAnswer(i)}
                      style={{ background: bg, border, borderRadius: 8, padding: "11px 14px", textAlign: "left", cursor: reviewAnswered ? "default" : "pointer", fontSize: 13, color, fontFamily: "inherit", lineHeight: 1.4 }}>
                      {ch}
                    </button>
                  );
                })}
              </div>
              {reviewAnswered && rq.explanation && (
                <div style={{ background: BLUE_LIGHT, border: "1px solid " + BLUE_LIGHT, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: TEXT1, lineHeight: 1.65, marginTop: 10 }}>
                  💡 {rq.explanation}
                </div>
              )}
              {reviewAnswered && (
                <button onClick={nextReview}
                  style={{ width: "100%", background: "#f97316", color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 12, fontFamily: "inherit" }}>
                  {reviewIdx < totalWrong - 1 ? t("次へ →", "Next →") : t("結果に戻る", "Back to results")}
                </button>
              )}
            </div>
          )}
        </div>
      </>);
    }

    // Result screen
    if (showResult && sessionQs.length > 0) {
      return wrap(<>
        <AppHeader backLabel={t("ステージ", "Stage")} onBack={resetToSubcat} />
        <div style={{ padding: "20px 16px" }}>
          {cleared ? (
            <div style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)", borderRadius: 14, padding: "24px 20px", textAlign: "center", color: "#fff", marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{t("ステージクリア！", "Stage Cleared!")}</div>
              <div style={{ fontSize: 32, fontWeight: 800, margin: "8px 0" }}>{correctCount}/{sessionQs.length}</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{t("次のステージ解放", "Next stage unlocked")}</div>
            </div>
          ) : (
            <div style={{ background: "linear-gradient(135deg, #f97316, #dc2626)", borderRadius: 14, padding: "24px 20px", textAlign: "center", color: "#fff", marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🦆</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{t("惜しい！", "Almost there!")}</div>
              <div style={{ fontSize: 32, fontWeight: 800, margin: "8px 0" }}>{correctCount}/{sessionQs.length}</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{t(`あと${CLEAR_THRESH - correctCount}問正解でクリア`, `${CLEAR_THRESH - correctCount} more correct to clear`)}</div>
            </div>
          )}
          {incorrects.length > 0 && (
            <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              ✗ {t(`${incorrects.length}問不正解 — 下で確認`, `${incorrects.length} incorrect answer${incorrects.length > 1 ? "s" : ""} — review them below`)}
            </div>
          )}
          {incorrects.length > 0 && (
            <button onClick={startReview}
              style={{ width: "100%", background: "transparent", border: "1px solid #f97316", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", color: "#f97316", marginBottom: 10 }}>
              📖 {t("不正解をレビュー", "Review incorrect answers")}
            </button>
          )}
          <button onClick={() => startStage(stage)}
            style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
            🔄 {t("新しい5問に挑戦", "Try 5 new questions")}
          </button>
          <button onClick={resetToSubcat}
            style={{ width: "100%", background: "none", border: "none", fontSize: 13, color: TEXT4, cursor: "pointer", padding: 8, fontFamily: "inherit" }}>
            {t("ステージ選択に戻る", "Back to stage selection")}
          </button>
        </div>
      </>);
    }

    // Quiz question screen
    if (stage !== null && sessionQs.length > 0) {
      return wrap(<>
        <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
          <button onClick={resetToSubcat} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: 0, fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}>← Stage {stage}</button>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={() => { resetToCountry(); setTab("home"); }} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", fontWeight: 600 }}>🏠</button>
            <HeaderRight />
          </div>
        </div>
        <div style={{ padding: "8px 16px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: TEXT3 }}>⭐ Stage {stage}</span>
            <div style={{ flex: 1, height: 4, background: BORDER, borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", width: (Math.min(qi + (answered ? 1 : 0), sessionQs.length) / sessionQs.length * 100) + "%", background: BLUE, borderRadius: 2, transition: "width .3s" }} />
            </div>
            <div style={{ fontSize: 11, color: TEXT4 }}>{Math.min(qi + (answered ? 1 : 0), sessionQs.length)}/{sessionQs.length}</div>
          </div>
          {qsLoading && <div style={{ textAlign: "center", padding: "40px 0", color: TEXT4 }}>{t("読み込み中...", "Loading...")}</div>}
          {!qsLoading && sessionQs.length === 0 && stage !== null && (
            <div style={{ textAlign: "center", padding: "40px 0", color: TEXT4 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div style={{ marginBottom: 8 }}>{t("このステージの問題はまだありません。", "No questions available for this stage yet.")}</div>
              <div style={{ fontSize: 11, color: TEXT4 }}>{quizCountry?.toUpperCase()} / {subcat} / Stage {stage}</div>
            </div>
          )}
          {!qsLoading && curQ && !showResult && (
            <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 11, color: "#bbb", marginBottom: 10 }}>Q{qi + 1} · Lv.{curQ.level}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: TEXT1, marginBottom: 16, lineHeight: 1.6 }}>{curQ.question}</div>
              <div style={{ display: "grid", gap: 8 }}>
                {(curQ.choices || []).map((ch, i) => {
                  let bg = SUBTLE, border = "1px solid " + BORDER, color = TEXT1;
                  if (answered) {
                    if (i === curQ.answer) { bg = darkMode ? "#14532d" : "#dcfce7"; border = "1.5px solid " + GREEN; color = darkMode ? "#86efac" : "#15803d"; }
                    else if (i === chosen) { bg = darkMode ? "#450a0a" : "#fee2e2"; border = "1.5px solid " + RED; color = darkMode ? "#fca5a5" : "#dc2626"; }
                  }
                  return (
                    <button key={i} onClick={() => answer(i)}
                      style={{ background: bg, border, borderRadius: 8, padding: "12px 14px", textAlign: "left", cursor: answered ? "default" : "pointer", fontSize: 13, color, fontFamily: "inherit", lineHeight: 1.45, transition: "background .2s" }}>
                      {ch}
                    </button>
                  );
                })}
              </div>
              {answered && curQ.explanation && (
                <div style={{ background: BLUE_LIGHT, border: "1px solid " + BLUE_LIGHT, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: TEXT1, lineHeight: 1.65, marginTop: 12 }}>
                  💡 {curQ.explanation}
                </div>
              )}
              {answered && qi < sessionQs.length - 1 && (
                <button onClick={nextQ}
                  style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 12, fontFamily: "inherit" }}>
                  {t("次の問題", "Next question")}
                </button>
              )}
            </div>
          )}
        </div>
      </>);
    }

    // Stage loading
    if (stage !== null && qsLoading) {
      return wrap(<>
        <AppHeader backLabel={gl(subcatInfo) || t("戻る", "Back")} onBack={resetToSubcat} />
        <div style={{ textAlign: "center", padding: "60px 0", color: TEXT4 }}>{t(`ステージ${stage}を読み込み中...`, `Loading stage ${stage}...`)}</div>
      </>);
    }

    // No questions found (Firestore returned empty after loading)
    if (stage !== null && !qsLoading && sessionQs.length === 0) {
      return wrap(<>
        <AppHeader />
        <div style={{ textAlign: "center", padding: "60px 20px", color: TEXT4 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 14, marginBottom: 6 }}>{t("このステージの問題はまだありません。", "No questions available for this stage yet.")}</div>
          <div style={{ fontSize: 11, color: TEXT4, marginBottom: 8 }}>{quizCountry?.toUpperCase()} / {subcat} / Stage {stage}</div>
          {qsError && <div style={{ fontSize: 11, color: "#ef4444", marginBottom: 12, wordBreak: "break-all", textAlign: "left", background: "#fef2f2", padding: "8px 10px", borderRadius: 8 }}>Error: {qsError}</div>}
          <button onClick={() => setStage(null)} style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {t("戻る", "Back")}
          </button>
        </div>
      </>);
    }

    // Stage selection screen
    if (subcat) {
      return wrap(<>
        <AppHeader />
        <div style={{ padding: "0 16px 12px" }}>
          <button onClick={resetToGroup} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: "12px 0", fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}>← {gl(groupInfo)}</button>
          {limits.quizPerDay !== Infinity && (() => {
            const used = getDailyCount("quiz");
            const left = Math.max(0, limits.quizPerDay - used);
            return (
              <div style={{ background: darkMode ? "#0d1f14" : "#f0fdf4", border: "1px solid " + (darkMode ? "#1a3a22" : "#bbf7d0"), borderRadius: 8, padding: "8px 12px", fontSize: 12, color: darkMode ? "#4ade80" : "#15803d", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                🟢 {t(`本日残り ${left} / ${limits.quizPerDay} 問`, `${left} / ${limits.quizPerDay} questions remaining today`)}
              </div>
            );
          })()}
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT1, marginBottom: 2 }}>{subcatInfo?.ic} {gl(subcatInfo)}</div>
          <div style={{ fontSize: 12, color: TEXT4, marginBottom: 16 }}>{t("ステージ1をクリアして次のステージを解放", "Clear Stage 1 to unlock the next stages")}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {STAGES.map((s, idx) => {
              const planLocked = s.id > limits.maxStage;
              const accessible = !planLocked;
              const unlocked = accessible && idx === 0;
              return (
                <div key={s.id} onClick={() => {
                  if (planLocked) { setUpgradeReason("stage"); setShowUpgrade(true); return; }
                  if (!checkLimit("quiz", "quiz")) return;
                  setStage(s.id); startStage(s.id);
                }}
                  style={{
                    background: CARD,
                    border: accessible ? "1px solid " + BLUE : "1px solid " + BORDER,
                    borderLeft: accessible ? "4px solid " + BLUE : "4px solid #f59e0b",
                    borderRadius: 12, padding: "16px",
                    cursor: accessible ? "pointer" : "default",
                    opacity: accessible ? 1 : 0.55,
                    display: "flex", alignItems: "center", gap: 14,
                  }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: accessible ? TEXT1 : TEXT3 }}>{appLang === "ja" ? s.ja_label : s.label}</div>
                    <div style={{ fontSize: 12, color: TEXT4, marginTop: 2 }}>Lv.{s.levels[0]}~{s.levels[1]}</div>
                    {planLocked && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 2 }}>✈️ {t("Voyager以上で解放", "Unlock with Voyager+")}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 1, fontSize: 16 }}>
                    {[...Array(s.id)].map((_, i) => <span key={i}>{i === s.id - 1 && s.id === 5 ? "👑" : "⭐"}</span>)}
                  </div>
                  {accessible && <span style={{ color: BLUE, fontSize: 18 }}>›</span>}
                  {planLocked && <span style={{ fontSize: 16 }}>🔒</span>}
                </div>
              );
            })}
          </div>
        </div>
      </>);
    }

    // Subcategory selection
    if (group) {
      return wrap(<>
        <AppHeader />
        <div style={{ padding: "0 16px 12px" }}>
          <button onClick={resetToCountry} style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: "12px 0", fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}>← {t("カテゴリに戻る", "Back to categories")}</button>
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT1, marginBottom: 14 }}>{groupInfo?.ic} {gl(groupInfo)}</div>
          <div style={{ display: "grid", gap: 8 }}>
            {(groupInfo?.subs || []).map(s => (
              <div key={s.id} onClick={() => setSubcat(s.id)}
                style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 24 }}>{s.ic}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: TEXT1 }}>{gl(s)}</div>
                </div>
                <span style={{ color: "#bbb", fontSize: 18 }}>›</span>
              </div>
            ))}
          </div>
        </div>
      </>);
    }

    // Group selection
    if (quizCountry) {
      return wrap(<>
        <AppHeader />
        <div style={{ padding: "12px 16px" }}>
          <div style={{ fontSize: 13, color: TEXT3, marginBottom: 12 }}>{t("カテゴリを選択", "Select a category")}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {GROUPS.map(g => (
              <div key={g.id} onClick={() => setGroup(g.id)}
                style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 14, padding: "16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: darkMode ? SUBTLE : g.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{g.ic}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: TEXT1 }}>{gl(g)}</div>
                  <div style={{ fontSize: 12, color: TEXT3, marginTop: 2 }}>{g.subs.map(s => gl(s)).slice(0, 4).join(" · ")}</div>
                </div>
                <span style={{ color: "#bbb", fontSize: 18 }}>›</span>
              </div>
            ))}
          </div>
        </div>
      </>);
    }

    // No country selected yet — show empty shell so modals (CountryPickModal) can render
    return wrap(<div style={{ padding: "40px 16px", textAlign: "center", color: TEXT4 }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🌍</div>
      <div style={{ fontSize: 14 }}>{t("国を選択して学習を始めましょう", "Select a country to get started")}</div>
      <button onClick={() => setShowCountryPick(true)}
        style={{ marginTop: 16, background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
        {t("国を選択", "Choose a country")}
      </button>
    </div>);
  }

  // ── ROLEPLAY (HOW) ──────────────────────────────────────────────

  // Roleplay conversation screen
  if (tab === "roleplay" && roleplayScene) {
    const sceneMenu = roleplayScene;
    const dialogue = SCENE_DIALOGUES[sceneMenu.id];
    if (!dialogue) {
      return wrap(<>
        <AppHeader backLabel={t("ロールプレイ", "Roleplay")} onBack={() => { setRoleplayScene(null); setRoleplaySubcat(true); }} />
        <div style={{ padding: "60px 20px", textAlign: "center", color: TEXT4 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚧</div>
          <div style={{ fontSize: 14 }}>{t("このシーンは準備中です", "This scene is coming soon")}</div>
        </div>
      </>);
    }
    const turn = dialogue.turns[roleplayTurnIdx];
    const totalTurns = dialogue.turns.length;
    const isLast = roleplayTurnIdx === totalTurns - 1;
    const situation = appLang === "ja" ? (dialogue.situationJa || dialogue.situation) : dialogue.situation;
    const chosenFeedback = roleplayChosen !== null
      ? (appLang === "ja"
          ? (turn.options[roleplayChosen].feedbackJa || turn.options[roleplayChosen].feedback)
          : (turn.options[roleplayChosen].feedbackEn || turn.options[roleplayChosen].feedback))
      : null;
    const turnText = (appLang === "ja" ? (turn.textJa || turn.text) : turn.text)
      .replace("{name}", user?.displayName?.split(" ")[0] || "Tanaka");

    function nextTurn() {
      if (isLast) {
        const bonus = 30;
        const newXP = totalXP + bonus;
        const oldLevel = levelFromXP(totalXP);
        const newLevel = levelFromXP(newXP);
        setTotalXP(newXP);
        setXpGain({ amount: bonus, key: Date.now() });
        if (newLevel > oldLevel) setLevelUp(newLevel);
        if (user) saveUserProfile(user.uid, { totalXP: newXP, level: newLevel });
        setRoleplayScene(null);
        setRoleplayTurnIdx(0);
        setRoleplayChosen(null);
        setRoleplayAnswered(false);
        setRoleplaySubcat(true);
      } else {
        setRoleplayTurnIdx(i => i + 1);
        setRoleplayChosen(null);
        setRoleplayAnswered(false);
      }
    }

    const speakLine = (text) => {
      if (!("speechSynthesis" in window)) return;
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = "en-GB";
      utt.rate = 0.95;
      window.speechSynthesis.speak(utt);
    };

    return wrap(<>
      <AppHeader backLabel={appLang === "ja" ? sceneMenu.label : (sceneMenu.label_en || sceneMenu.label)} onBack={() => { setRoleplayScene(null); setRoleplayTurnIdx(0); setRoleplayChosen(null); setRoleplayAnswered(false); setRoleplaySubcat(true); }} />

      <div style={{ padding: "12px 16px 24px" }}>
        {/* Progress */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 4, background: BORDER, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: ((roleplayTurnIdx) / totalTurns * 100) + "%", background: BLUE, borderRadius: 2, transition: "width .3s" }} />
          </div>
          <span style={{ fontSize: 11, color: TEXT4, flexShrink: 0 }}>{roleplayTurnIdx + 1}/{totalTurns}</span>
        </div>

        {/* Situation */}
        <div style={{ fontSize: 13, color: "#e05a00", marginBottom: 16, lineHeight: 1.5 }}>
          📍 {situation}
        </div>

        {/* Character + speech */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <div style={{ width: 54, height: 54, borderRadius: "50%", overflow: "hidden", border: "2px solid " + BORDER, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>
              <img
                src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(turn.name || "user")}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                alt={turn.name}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
            <div onClick={() => speakLine(turnText)}
              style={{ fontSize: 11, fontWeight: 600, color: TEXT2, cursor: "pointer", display: "flex", alignItems: "center", gap: 2, padding: "2px 6px", borderRadius: 10, background: BLUE_LIGHT }}>
              {turn.name} <span style={{ fontSize: 12 }}>🔊</span>
            </div>
          </div>
          <div style={{ flex: 1, background: SUBTLE, border: "1px solid #e8e8e8", borderRadius: "4px 12px 12px 12px", padding: "12px 14px", fontSize: 14, color: TEXT1, lineHeight: 1.6, fontStyle: "italic" }}>
            {turnText}
          </div>
        </div>

        {/* Response choices */}
        <div style={{ fontSize: 12, color: TEXT3, fontWeight: 600, marginBottom: 8 }}>{t("あなたの回答：", "Your response:")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {turn.options.map((opt, i) => {
            let borderColor = BORDER;
            let bgColor = CARD;
            if (roleplayAnswered) {
              if (i === roleplayChosen) {
                borderColor = opt.correct ? "#22c55e" : "#ef4444";
                bgColor = opt.correct ? (darkMode ? "#14532d" : "#f0fdf4") : (darkMode ? "#450a0a" : "#fef2f2");
              } else if (opt.correct) {
                borderColor = "#22c55e";
                bgColor = darkMode ? "#14532d" : "#f0fdf4";
              }
            }
            return (
              <div key={i}
                onClick={() => {
                  if (!roleplayAnswered) {
                    setRoleplayChosen(i);
                    setRoleplayAnswered(true);
                    if (opt.correct) {
                      const rpXP = 5;
                      const newXP = totalXP + rpXP;
                      const oldLevel = levelFromXP(totalXP);
                      const newLevel = levelFromXP(newXP);
                      setTotalXP(newXP);
                      setXpGain({ amount: rpXP, key: Date.now() });
                      if (newLevel > oldLevel) setLevelUp(newLevel);
                      if (user) saveUserProfile(user.uid, { totalXP: newXP, level: newLevel });
                    }
                  }
                }}
                style={{ background: bgColor, border: "1.5px solid " + borderColor, borderRadius: 10, padding: "13px 14px", fontSize: 13, color: TEXT1, lineHeight: 1.5, cursor: roleplayAnswered ? "default" : "pointer", transition: "all .15s" }}>
                {appLang === "ja" ? (opt.textJa || opt.text) : opt.text}
              </div>
            );
          })}
        </div>

        {/* Per-option feedback + Next button */}
        {roleplayAnswered && (
          <div style={{ marginTop: 14 }}>
            <div style={{ background: darkMode ? "#2a2010" : "#fffbeb", border: "1px solid " + (darkMode ? "#3a3010" : "#fde68a"), borderRadius: 10, padding: "10px 14px", fontSize: 12, color: darkMode ? "#fcd34d" : "#92400e", lineHeight: 1.5, marginBottom: 12 }}>
              {chosenFeedback}
            </div>
            <button onClick={nextTurn} style={{ width: "100%", background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: 13, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              {isLast ? t("完了 ✓", "Finish ✓") : t("次へ →", "Next →")}
            </button>
          </div>
        )}
      </div>
    </>);
  }

  if (tab === "roleplay" && roleplaySubcat && group) {
    const groupScenes = SCENE_MENU.filter(sc =>
      sc.catId === group &&
      sc.available !== false &&
      SCENE_DIALOGUES[sc.id] &&
      (!sc.countries?.length || sc.countries.includes(quizCountry))
    );
    return wrap(<>
      <AppHeader />
      <div style={{ padding: "0 16px 24px" }}>
        <button onClick={() => { setRoleplaySubcat(false); setGroup(null); }}
          style={{ background: "none", border: "none", fontSize: 13, color: BLUE, cursor: "pointer", padding: "12px 0", fontWeight: 600 }}>
          ← {t("カテゴリ", "Categories")}
        </button>
        <div style={{ fontSize: 18, fontWeight: 700, color: TEXT1, marginBottom: 4 }}>{groupInfo?.ic} {gl(groupInfo)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: TEXT3, marginBottom: 12 }}>
          <span>🌐</span>
          <span>{t("現地語で応答を練習します。解説は日本語で表示されます。", "Practice responding in the local language. Notes shown in English.")}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: TEXT2 }}>{t("会話練習", "Conversation Practice")}</span>
          <span style={{ fontSize: 11, color: BLUE, fontWeight: 600 }}>{t("選択式 · Pro+", "Multiple choice · Pro+")}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {groupScenes.map(sc => {
            return (
              <div key={sc.id}
                onClick={() => {
                  if (!checkLimit("roleplay", "roleplay")) return;
                  incDailyCount("roleplay");
                  setRoleplayScene(sc);
                  setRoleplayTurnIdx(0);
                  setRoleplayChosen(null);
                  setRoleplayAnswered(false);
                }}
                style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                  {sc.ic || "💬"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: TEXT1 }}>{appLang === "ja" ? sc.label : (sc.label_en || sc.label)}</div>
                  <div style={{ fontSize: 11, color: TEXT4, marginTop: 2 }}>{appLang === "ja" ? sc.desc : (sc.desc_en || sc.desc)}</div>
                </div>
                {false
                  ? <div></div>
                  : <span style={{ color: "#bbb", fontSize: 18 }}>›</span>
                }
              </div>
            );
          })}
        </div>
        {/* AI Conversation Practice section */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: TEXT1 }}>{t("AI会話練習", "AI Conversation Practice")}</span>
            <div style={{ background: BLUE, color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>Early Access</div>
          </div>
          <div style={{ background: "#eef1ff", border: "1px solid #c7d2fe", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#3730a3", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span>🌐</span>
            <span>{t("AI会話練習はEarly Accessでご利用いただけます。", "AI conversation practice is available with Early Access.")}</span>
            <span onClick={() => setShowUpgrade(true)} style={{ color: BLUE, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{t("詳細 →", "Details →")}</span>
          </div>
          <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, opacity: 0.8 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: TEXT1 }}>{t("ビジネス交渉ロールプレイ", "Business Negotiation Roleplay")}</div>
              <div style={{ fontSize: 11, color: TEXT4, marginTop: 2 }}>{t("AIがパートナー。自由入力で練習。", "AI as your partner. Free input practice.")}</div>
            </div>
            <div style={{ background: BLUE, color: "#fff", borderRadius: 20, padding: "4px 10px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer" }} onClick={() => setShowUpgrade(true)}>Early Access</div>
          </div>
        </div>
      </div>
    </>);
  }

  if (tab === "roleplay") return wrap(<>
    <AppHeader />
    <div style={{ padding: "0 16px" }}>
      <div style={{ background: darkMode ? "#0d1f14" : "#f0fdf4", border: "1px solid " + (darkMode ? "#1a3a22" : "#bbf7d0"), borderRadius: 8, padding: "10px 14px", fontSize: 12, color: darkMode ? "#4ade80" : "#15803d", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <span>🟢</span>
        <span>{t("HOWシーン：本日 1 / 1 — ", "HOW scenes: 1 / 1 today — ")}</span>
        <span style={{ color: BLUE, cursor: "pointer" }} onClick={() => setShowUpgrade(true)}>{t("無制限にアップグレード", "Upgrade for unlimited")}</span>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {GROUPS.map(g => (
          <div key={g.id} onClick={() => { setGroup(g.id); setRoleplaySubcat(true); }}
            style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: 14, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: darkMode ? SUBTLE : g.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{g.ic}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT1 }}>{gl(g)}</div>
              <div style={{ fontSize: 12, color: TEXT3, marginTop: 4 }}>{gd(g)}</div>
            </div>
            <span style={{ color: "#bbb", fontSize: 18 }}>›</span>
          </div>
        ))}
      </div>
    </div>
  </>);

  // ── AI CHAT ─────────────────────────────────────────────────────

  if (tab === "ai") return wrap(<>
    <AppHeader backLabel={t("ホーム", "Home")} onBack={() => { setShowProfile(false); setTab("home"); }} />
    {/* Remaining count bar */}
    {(() => {
      const aiUsed = getDailyCount("ai");
      const aiMax = limits.aiPerDay === Infinity ? "∞" : limits.aiPerDay;
      const aiLeft = limits.aiPerDay === Infinity ? "∞" : Math.max(0, limits.aiPerDay - aiUsed);
      return (
        <div style={{ background: CARD, borderBottom: "1px solid " + BORDER, padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#22c55e", fontWeight: 600 }}>
            <span style={{ fontSize: 14 }}>🤖</span>
            <span>{t(`${aiLeft} / ${aiMax} 本日残り`, `${aiLeft} / ${aiMax} remaining today`)}</span>
          </div>
          {canUpgrade && <button onClick={() => { setUpgradeReason(null); setShowUpgrade(true); }} style={{ background: "none", border: "none", color: BLUE, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{t("アップグレード →", "Upgrade →")}</button>}
        </div>
      );
    })()}
    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", height: "calc(100vh - 110px)" }}>
      {/* Chat messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 8 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8 }}>
            {m.role === "assistant" && (
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #f97316, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>🤖</div>
            )}
            <div style={{ maxWidth: "80%", background: m.role === "user" ? BLUE : CARD, color: m.role === "user" ? "#fff" : TEXT1, border: m.role === "assistant" ? "1px solid " + BORDER : "none", borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "2px 12px 12px 12px", padding: "10px 12px", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {m.role === "assistant" && i === 0 ? t("こんにちは！世界各国のビジネス文化、日常生活、旅行マナーについて何でも聞いてください。", m.text) : m.text}
            </div>
          </div>
        ))}
        {aiLoading && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #f97316, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🤖</div>
            <div style={{ background: CARD, border: "1px solid " + BORDER, borderRadius: "2px 12px 12px 12px", padding: "10px 14px", fontSize: 13, color: TEXT4 }}>{t("考え中…", "Thinking…")}</div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      {/* Example question chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingBottom: 6, paddingTop: 4, flexShrink: 0 }}>
        {(appLang === "ja"
          ? ["英国のオフィスドレスコード？", "ドイツのビジネスダイニング", "米国のチップ文化", "ラマダン中のUAE", "英国の学校行事のコツ？"]
          : ["UK office dress code?", "German business dining", "Tipping in the USA", "UAE during Ramadan", "Tips for UK school events?"]
        ).map(q => (
          <div key={q} onClick={() => setInput(q)}
            style={{ background: BLUE_LIGHT, border: "1px solid " + BLUE, borderRadius: 20, padding: "5px 12px", fontSize: 11, color: BLUE, cursor: "pointer", fontWeight: 500 }}>
            {q}
          </div>
        ))}
      </div>
      {/* Input */}
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && sendAI()}
          placeholder={t("質問を入力...", "Ask a question...")}
          style={{ flex: 1, border: "1px solid " + BORDER, borderRadius: 10, padding: "11px 14px", fontSize: 13, fontFamily: "inherit", outline: "none", background: CARD, color: TEXT1 }} />
        <button onClick={sendAI} disabled={!input.trim() || aiLoading}
          style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: "11px 18px", fontSize: 13, cursor: "pointer", fontWeight: 700, opacity: (!input.trim() || aiLoading) ? 0.5 : 1 }}>
          {t("送信", "Send")}
        </button>
      </div>
    </div>
  </>);

  return null;
}
